// biologyGCSE.js — AQA-aligned KS4 Biology (Y10/Y11). Sections B1-B7.
// Compact factory; every topic is exam-board-aligned. RPs flagged inline.

// Tier labels: 'foundation' (Combined Science) | 'higher' (Combined Higher / Triple)
const gt = (id, slug, title, c) => ({
  id, slug, title,
  difficulty: c.tier || 'foundation',
  pageRef: c.pageRef || '',
  ...(c.diagram ? { diagram: c.diagram } : {}),
  learn: {
    ...(c.intro ? { explanation: c.intro } : {}),
    ...(c.chunks ? { chunks: c.chunks } : {}),
    keyPoints: c.keyPoints || [],
    ...(c.we ? { workedExamples: Array.isArray(c.we) ? c.we : [c.we] } : {}),
    ...(c.whyItWorks ? { whyItWorks: c.whyItWorks } : {}),
  },
  glossary: c.glossary || [],
  ...(c.rp ? { requiredPractical: c.rp } : {}),
  ...(c.examQ ? { examQuestion: c.examQ } : {}),
  ...(c.examinerInsights ? { examinerInsights: c.examinerInsights } : {}),
  practice: {
    warmUp: c.practice?.warmUp || [],
    level1: c.practice?.level1 || [],
    level2: c.practice?.level2 || [],
    level3: c.practice?.level3 || [],
  },
});

// Quick practice helpers — keep declarations short
const tQ = (id, q, a, h1, h2, h3) => ({ id, type: 'text', question: q, answer: a, hint1: h1, hint2: h2, hint3: h3 });
const nQ = (id, q, a, h1, h2, h3) => ({ id, type: 'number', question: q, answer: a, hint1: h1, hint2: h2, hint3: h3 });
const cQ = (id, q, opts, a, h1, h2, h3) => ({ id, type: 'choice', question: q, options: opts, answer: a, hint1: h1, hint2: h2, hint3: h3 });

// =============================================================
// B1 — Cell Biology
// =============================================================
export const b1CellBiologyTopics = [
  gt('gcse-b1-1', 'eukaryotic-prokaryotic-cells', 'Eukaryotic & Prokaryotic Cells', {
    pageRef: 'B1.1',
    intro: 'Living things are made of one of two cell types. Eukaryotic cells are larger and have a nucleus inside a membrane (animal, plant, fungal cells). Prokaryotic cells are smaller, simpler and have no true nucleus — bacteria are the most common example. Both types have a cell membrane, cytoplasm, ribosomes and DNA, but they differ in where the DNA sits and what other organelles they carry.',
    keyPoints: [
      'Eukaryotic cells (animal, plant, fungus) have a nucleus that holds the DNA',
      'Prokaryotic cells (bacteria) have no nucleus — DNA is a single loop in the cytoplasm + extra plasmid rings',
      'Prokaryotes are typically 1-10 µm; eukaryotes 10-100 µm',
      'Both cell types share: cell membrane, cytoplasm, ribosomes, DNA',
      'Order-of-magnitude: 1 mm = 10⁻³ m, 1 µm = 10⁻⁶ m, 1 nm = 10⁻⁹ m',
    ],
    we: {
      problem: 'A bacterium is 2 µm long. Express this in metres in standard form.',
      steps: [
        { step: 1, instruction: '1 µm = 10⁻⁶ m', working: '2 µm = 2 × 10⁻⁶ m' },
        { step: 2, instruction: 'Already in standard form', working: '2 × 10⁻⁶ m' },
      ],
      answer: '2 × 10⁻⁶ m',
      commonMistake: 'Writing 2⁻⁶ m or 2 × 10⁶ m. The exponent is negative because micrometres are SMALLER than metres.',
    },
    glossary: [
      { term: 'Eukaryotic', definition: 'Cells with a nucleus enclosed in a membrane.' },
      { term: 'Prokaryotic', definition: 'Cells with no membrane-bound nucleus; DNA loose in cytoplasm.' },
      { term: 'Plasmid', definition: 'A small circular DNA molecule found in bacterial cells, separate from the main chromosome.' },
      { term: 'Ribosome', definition: 'Tiny structure where proteins are made. Found in BOTH cell types.' },
      { term: 'Standard form', definition: 'A × 10ⁿ where 1 ≤ A < 10. Used for very small or very large numbers.' },
    ],
    examQ: {
      prompt: 'Compare the structure of a typical bacterial cell with a typical animal cell. Give three similarities and three differences. (6 marks)',
      maxMarks: 6,
      markSchemePoints: [
        'Both have cell membrane / both have cytoplasm / both have ribosomes / both have DNA — any 3',
        'Bacterial DNA is loose / single loop, animal DNA is in a nucleus',
        'Bacteria have plasmids; animal cells do not',
        'Bacteria typically 1-10 µm, animal cells 10-100 µm (size difference)',
        'Bacteria may have a flagellum / cell wall (peptidoglycan); animal cells have neither',
      ],
    },
    practice: {
      warmUp: [
        cQ('b1-1.w1', 'Which cell type has a true nucleus?', ['Prokaryote', 'Eukaryote', 'Both', 'Neither'], 'Eukaryote', 'Eu = true', 'Animal/plant cells', 'Has membrane around DNA'),
        tQ('b1-1.w2', 'Name the small DNA loops found in bacteria.', 'plasmid', 'Extra rings of DNA', 'P_______', 'Means "moulded next to"'),
      ],
      level1: [
        cQ('b1-1.1', 'Which is shared by BOTH cell types?', ['Nucleus', 'Plasmid', 'Ribosome', 'Chloroplast'], 'Ribosome', 'Where proteins made', 'In cytoplasm of both', '8 letters'),
        nQ('b1-1.2', 'A prokaryote is 5 µm long. Express in m (standard form). Give the coefficient only.', '5', 'µm = 10⁻⁶ m', '5 × 10⁻⁶ m', 'Coefficient is the number before ×'),
      ],
      level2: [
        cQ('b1-1.3', 'A cell is 0.000004 m wide. In µm?', ['0.4 µm', '4 µm', '40 µm', '400 µm'], '4 µm', '× 10⁶', '4 × 10⁻⁶ m = 4 µm', 'Move decimal 6 places right'),
        tQ('b1-1.4', 'In which cell type is the DNA enclosed in a nuclear membrane? (one word)', 'eukaryotic', 'Means true-nucleus', 'Animals + plants', 'Eu...'),
      ],
    },
  }),

  gt('gcse-b1-2', 'animal-plant-cells-gcse', 'Animal & Plant Cells (GCSE)', {
    pageRef: 'B1.2',
    diagram: 'animalPlantCells',
    intro: 'Animal cells contain a nucleus, cytoplasm, cell membrane, mitochondria and ribosomes. Plant cells share all of these PLUS three extras: a cell wall (cellulose) for support, chloroplasts for photosynthesis, and a permanent vacuole filled with cell sap.',
    keyPoints: [
      'Animal cell: nucleus, cytoplasm, cell membrane, mitochondria, ribosomes',
      'Plant cell additions: cell wall (cellulose), chloroplasts, permanent vacuole',
      'Mitochondria — site of aerobic respiration',
      'Chloroplasts — contain chlorophyll for photosynthesis',
      'Cell wall is made of cellulose; provides structural support',
    ],
    glossary: [
      { term: 'Mitochondrion', definition: 'Site of aerobic respiration, where most ATP is made.' },
      { term: 'Chloroplast', definition: 'Green organelle in plant cells where photosynthesis happens.' },
      { term: 'Cellulose', definition: 'Tough polysaccharide that makes up the plant cell wall.' },
      { term: 'Vacuole', definition: 'Permanent fluid-filled sac in plant cells, full of cell sap; keeps cell turgid.' },
      { term: 'Cytoplasm', definition: 'Jelly-like fluid where most chemical reactions of the cell happen.' },
    ],
    practice: {
      warmUp: [
        cQ('b1-2.w1', 'Which is found in BOTH animal AND plant cells?', ['Cell wall', 'Mitochondria', 'Chloroplast', 'Vacuole'], 'Mitochondria', 'Energy organelle', 'Powerhouse of the cell', 'Plural'),
        tQ('b1-2.w2', 'What is the plant cell wall made of? (one word)', 'cellulose', 'A fibrous carbohydrate', 'Polymer of glucose', 'C_________'),
      ],
      level1: [
        cQ('b1-2.1', 'Which structure performs photosynthesis?', ['Mitochondrion', 'Chloroplast', 'Nucleus', 'Ribosome'], 'Chloroplast', 'Green organelle', 'Contains chlorophyll', 'Plant only'),
        cQ('b1-2.2', 'A cell has a cell wall and chloroplasts but no permanent vacuole. Most likely:', ['Animal', 'Plant leaf', 'Bacterium', 'Algal cell'], 'Algal cell', 'Has chloroplast + cell wall', 'Single-celled', 'Not bacterial'),
      ],
      level2: [
        tQ('b1-2.3', 'Which organelle in BOTH plants and animals carries out aerobic respiration?', 'mitochondria', 'Powerhouse', 'Plural form expected', 'M____________'),
      ],
    },
  }),

  gt('gcse-b1-3', 'cell-specialisation-differentiation', 'Cell Specialisation & Differentiation', {
    pageRef: 'B1.3',
    intro: 'Cells are differentiated — they become specialised in shape and contents to match a job. Once differentiated in animals, most cells stay that way; in plants, cells can re-differentiate throughout life. Examples: sperm (long tail + many mitochondria), nerve cells (long axon), root hair cells (large surface area), red blood cells (no nucleus, biconcave, packed with haemoglobin).',
    keyPoints: [
      'Differentiation = a cell becoming specialised',
      'Most animal cells differentiate at an early stage; plant cells throughout life',
      'Sperm: tail (movement), many mitochondria (energy), enzymes in the head',
      'Nerve cell: long axon, branching dendrites, fast electrical signals',
      'Root hair cell: long extension increases surface area for water and ion uptake',
      'Red blood cell: no nucleus + biconcave shape = max haemoglobin and surface area',
    ],
    glossary: [
      { term: 'Differentiation', definition: 'The process by which a cell becomes specialised for its function.' },
      { term: 'Specialised cell', definition: 'A cell adapted to do a particular job.' },
      { term: 'Haemoglobin', definition: 'Iron-containing protein in red blood cells that binds oxygen.' },
      { term: 'Axon', definition: 'Long extension of a nerve cell that carries electrical signals.' },
    ],
    practice: {
      warmUp: [
        tQ('b1-3.w1', 'A sperm cell has many of which organelle for energy?', 'mitochondria', 'Powerhouse', 'Plural', 'M___________'),
        cQ('b1-3.w2', 'Why do red blood cells lack a nucleus?', ['Saves energy', 'More room for haemoglobin', 'Smaller cells', 'Faster division'], 'More room for haemoglobin', 'They carry oxygen', 'Concentration matters', 'About function'),
      ],
      level1: [
        cQ('b1-3.1', 'Which cell adaptation increases surface area?', ['Long extensions on a root hair cell', 'A nucleus', 'A cell wall', 'A flagellum'], 'Long extensions on a root hair cell', 'Stretches outwards', 'For water uptake', 'Plant cell adaptation'),
      ],
      level2: [
        tQ('b1-3.2', 'Differentiation that allows a cell to specialise happens early in animals but in plants it happens ___.', 'throughout life', 'Plants keep doing it', 'Lifelong', 'Two words'),
      ],
    },
  }),

  gt('gcse-b1-4', 'microscopy-magnification', 'Microscopy & Magnification (RP1)', {
    pageRef: 'B1.4',
    diagram: 'microscope',
    intro: 'Microscopes let us see cells. The TWO key numbers are magnification (how much bigger) and resolution (smallest detail you can distinguish). Light microscopes magnify up to ×2000; electron microscopes can go beyond ×500,000 with much higher resolution, revealing sub-cellular structures.',
    keyPoints: [
      'Total magnification = eyepiece × objective',
      'Magnification = image size ÷ real size',
      'Real size = image size ÷ magnification',
      'Resolution = the smallest distance between two points that can still be seen as separate',
      'Light microscope: max ×2000 magnification, resolution ~200 nm',
      'Electron microscope: ×500,000+, resolution ~0.2 nm — sees ribosomes, mitochondria detail',
    ],
    we: [
      {
        problem: 'A cell is 0.05 mm long. Through a microscope it appears 50 mm. Calculate magnification.',
        steps: [
          { step: 1, instruction: 'Same units', working: '0.05 mm and 50 mm — already match' },
          { step: 2, instruction: 'M = image ÷ real', working: '50 ÷ 0.05' },
          { step: 3, instruction: 'Evaluate', working: '= 1000' },
        ],
        answer: '×1000',
        commonMistake: 'Forgetting to convert units. If image is in mm and real is in µm, you MUST convert first.',
      },
      {
        problem: 'A cell appears 60 mm wide at ×400. Calculate the real size in µm.',
        steps: [
          { step: 1, instruction: 'Convert image to µm', working: '60 mm = 60,000 µm' },
          { step: 2, instruction: 'Real = image ÷ M', working: '60,000 ÷ 400' },
          { step: 3, instruction: 'Evaluate', working: '= 150 µm' },
        ],
        answer: '150 µm',
      },
    ],
    glossary: [
      { term: 'Magnification', definition: 'How many times bigger the image is than the real object.' },
      { term: 'Resolution', definition: 'Smallest distance between two points that appear separate.' },
      { term: 'Eyepiece', definition: 'Top lens, usually ×10.' },
      { term: 'Objective lens', definition: 'Lens nearest the specimen. Usually ×4, ×10 or ×40.' },
    ],
    rp: {
      title: 'Required Practical 1: Use of a light microscope',
      aqaRef: 'AQA Required Practical (Biology) 1',
      aim: 'Prepare a stained slide of onion epidermal cells and observe at three different magnifications.',
      method: [
        'Peel a small section of epidermis from inside an onion layer.',
        'Place the specimen flat on a clean slide.',
        'Add 1 drop of iodine solution as a stain.',
        'Lower a cover slip at an angle using a mounted needle to avoid air bubbles.',
        'Place the slide on the stage; clip in place.',
        'Use the lowest-power objective (×4) first; coarse focus until cells are visible.',
        'Switch to ×10 then ×40; use fine focus only at higher magnifications.',
        'Calculate total magnification (eyepiece × objective) and label your drawing.',
      ],
      safety: 'Iodine stains skin — wash off immediately. Glass slides break sharply — handle with care.',
      tips: [
        'Always lower the stage AWAY from the objective when focusing — never upwards.',
        'Choose a cell from the centre of the field of view (less distortion).',
        'Use a sharp pencil; clean unbroken lines; NO shading.',
      ],
    },
    examQ: {
      prompt: 'A student observes a plant cell at total magnification ×400. The image of the cell measures 32 mm in diameter. Calculate the real diameter of the cell in micrometres. Show your working. (3 marks)',
      maxMarks: 3,
      markSchemePoints: [
        'Convert 32 mm = 32,000 µm OR keep in mm and convert at end',
        'Real = image ÷ magnification = 32,000 ÷ 400',
        'Real = 80 µm',
      ],
    },
    examinerInsights: {
      whyMarksLost: [
        'Forgetting to convert units before dividing — examiners want both quantities in the SAME unit (mm OR µm).',
        'Calculating image ÷ image instead of image ÷ magnification.',
        'Giving the answer without units — every numeric answer must have a unit at GCSE.',
        'Rounding too early in the calculation — keep at least 3 sig fig until the final line.',
      ],
      whatExaminersWant: [
        'Two visible working lines: ONE for unit conversion, ONE for the formula.',
        'Final answer with the correct unit (µm here) and to a sensible number of significant figures.',
        'A clear formula recall — write "Real = image ÷ magnification" before substituting.',
      ],
      commonTraps: [
        'Magnification is unitless — putting "×400 µm" loses a mark.',
        'A diagram says ×400 but the image is given in mm — students forget to convert.',
        'Negative powers of 10 (× 10⁻⁶) for µm vs × 10⁻³ for mm — flip these and the answer is 1000× wrong.',
      ],
      gradeNineMove: 'Always state the formula as a separate line, then unit-convert as a second line, then substitute and evaluate. That habit alone is worth 1 mark on calculation questions across the whole spec.',
    },
    practice: {
      warmUp: [
        nQ('b1-4.w1', '×10 eyepiece, ×40 objective. Total magnification?', '400', 'Multiply', '10 × 40', '3 digits'),
        tQ('b1-4.w2', 'Smallest distance two points can be seen as separate is called ___.', 'resolution', 'R________', 'Not magnification', '"Resolves"'),
      ],
      level1: [
        nQ('b1-4.1', 'Image = 40 mm, real = 0.1 mm. Magnification?', '400', 'M = image ÷ real', '40 ÷ 0.1', '3 digits'),
        nQ('b1-4.2', 'Image = 25 mm, M = ×500. Real size in µm?', '50', 'Real = image ÷ M', '25 mm = 25,000 µm; ÷ 500', '50 µm'),
      ],
      level2: [
        nQ('b1-4.3', 'A cell is 12 µm wide and the image is 6 mm wide. Magnification?', '500', 'Convert to same units', '6 mm = 6000 µm; 6000 ÷ 12', '3 digits'),
        cQ('b1-4.4', 'Light microscope max magnification is around:', ['×200', '×2000', '×20,000', '×200,000'], '×2000', 'Above this resolution fails', 'Wavelength of light limits it', '4-digit number'),
      ],
      level3: [
        nQ('b1-4.5', 'A nerve cell is 1 mm long. Calculate length in µm.', '1000', '1 mm = 1000 µm', 'Move decimal 3 places', '4 digits'),
      ],
    },
  }),

  gt('gcse-b1-5', 'stem-cells', 'Stem Cells', {
    pageRef: 'B1.5',
    tier: 'higher',
    intro: 'Stem cells are unspecialised cells that can divide to produce more stem cells AND can differentiate into specialised cells. Embryonic stem cells (from early embryos) can become any type of cell. Adult stem cells (e.g. in bone marrow) can become a limited range — mainly blood cells. Plant meristems contain stem cells throughout life. Therapeutic uses: replacing damaged tissues, treating diabetes, paralysis. Ethical issues surround embryonic use.',
    keyPoints: [
      'Stem cells = unspecialised cells able to divide AND differentiate',
      'Embryonic stem cells: pluripotent — can become any type',
      'Adult stem cells (e.g. bone marrow): can only become certain types (e.g. blood)',
      'Plant meristems: stem cells in growing tips of roots and shoots',
      'Therapeutic cloning produces an embryo with the patient\'s genes — no rejection risk',
      'Ethical concern: embryos are destroyed; some object on religious / moral grounds',
      'Risks: contamination by viruses, uncontrolled division (cancer)',
    ],
    glossary: [
      { term: 'Stem cell', definition: 'Unspecialised cell that can divide and differentiate.' },
      { term: 'Embryonic stem cell', definition: 'Stem cell from a 4-5 day old embryo; can become any cell type.' },
      { term: 'Adult stem cell', definition: 'Stem cell in adult tissue (e.g. bone marrow); limited range of cell types.' },
      { term: 'Meristem', definition: 'Region of plant where stem cells divide; tips of shoots and roots.' },
      { term: 'Therapeutic cloning', definition: 'Producing an embryo with the patient\'s genes to harvest matching stem cells.' },
    ],
    practice: {
      warmUp: [
        cQ('b1-5.w1', 'A stem cell can:', ['Only divide', 'Only differentiate', 'Both divide and differentiate', 'Neither'], 'Both divide and differentiate', 'Two abilities', 'Defines a stem cell', 'Both'),
        tQ('b1-5.w2', 'Plant region where cells continue to divide is called a ___.', 'meristem', 'In root and shoot tips', 'Plant equivalent of stem cell zone', 'M_______'),
      ],
      level1: [
        cQ('b1-5.1', 'Adult stem cells are LIMITED in what they can become. Why use them?', ['Cheaper', 'Patient\'s own — no rejection', 'They divide faster', 'No ethical issues with embryos'], 'No ethical issues with embryos', 'Big ethical advantage', 'Adult vs embryonic', 'Avoids embryo destruction'),
      ],
      level2: [
        tQ('b1-5.2', 'Type of stem cell that can become ANY type of cell? (one word)', 'embryonic', 'From early embryos', 'Pluripotent', 'E________'),
      ],
    },
  }),

  gt('gcse-b1-6', 'cell-cycle-mitosis', 'The Cell Cycle & Mitosis', {
    pageRef: 'B1.6',
    intro: 'Body cells divide by mitosis to produce two genetically identical daughter cells. The cell cycle has 3 stages: (1) Growth & DNA replication — organelles double, chromosomes copy themselves. (2) Mitosis — the nucleus divides; one set of chromosomes goes to each end of the cell. (3) Cytokinesis — the cell membrane (and wall in plants) pinches in to split the cytoplasm. Mitosis is used for growth, repair and asexual reproduction.',
    keyPoints: [
      'Mitosis produces 2 genetically identical diploid daughter cells',
      'Stage 1: DNA replicates → each chromosome becomes two sister chromatids',
      'Stage 2: Mitosis — chromosomes line up on the equator, sister chromatids pulled apart',
      'Stage 3: Cytokinesis — cytoplasm divides',
      'Used for: growth, repair, replacement of cells, asexual reproduction',
      'Uncontrolled mitosis = cancer (tumours)',
    ],
    glossary: [
      { term: 'Mitosis', definition: 'Cell division producing two genetically identical diploid cells.' },
      { term: 'Chromosome', definition: 'A long DNA molecule wrapped around proteins; carries genes.' },
      { term: 'Chromatid', definition: 'One copy of a duplicated chromosome, joined to its sister at the centromere.' },
      { term: 'Cytokinesis', definition: 'Splitting of the cytoplasm at the end of cell division.' },
      { term: 'Diploid', definition: 'Has two complete sets of chromosomes (in humans, 46).' },
    ],
    practice: {
      warmUp: [
        cQ('b1-6.w1', 'Mitosis produces:', ['2 identical cells', '4 different cells', '2 different cells', '4 identical cells'], '2 identical cells', 'Mit = "two"', 'Same DNA', 'Two and identical'),
        tQ('b1-6.w2', 'Splitting of the cytoplasm is called ___.', 'cytokinesis', 'Cyto = cell, kinesis = movement', 'After mitosis', 'C__________'),
      ],
      level1: [
        cQ('b1-6.1', 'Number of chromosomes in a human body cell?', ['23', '46', '92', '8'], '46', 'Diploid', '23 pairs', 'Twice 23'),
        cQ('b1-6.2', 'Mitosis is NOT used for:', ['Growth', 'Repair', 'Producing gametes', 'Asexual reproduction'], 'Producing gametes', 'Gametes need meiosis', 'Halving chromosomes', 'Sexual reproduction'),
      ],
      level2: [
        tQ('b1-6.3', 'Uncontrolled mitosis can lead to ___.', 'cancer', 'Lump of cells', 'Tumour', '6 letters'),
      ],
    },
  }),

  gt('gcse-b1-7', 'transport-in-cells', 'Diffusion, Osmosis & Active Transport (RP2)', {
    pageRef: 'B1.7',
    intro: 'Cells exchange substances with their surroundings in three main ways. Diffusion: net movement of particles from high to low concentration (passive). Osmosis: net movement of WATER through a partially permeable membrane from dilute to concentrated solute (passive). Active transport: movement against a concentration gradient using energy from respiration (e.g. mineral ion uptake in plant roots, glucose absorption in the gut).',
    keyPoints: [
      'Diffusion: high → low concentration; passive (no energy)',
      'Osmosis: water through partially permeable membrane, dilute → concentrated solution; passive',
      'Active transport: against the gradient; needs ATP from respiration',
      'Rate of diffusion ↑ with steeper gradient, higher temperature, larger surface area',
      'Surface area : volume ratio is critical — small organisms diffuse directly; larger ones need transport systems',
      'Plant root hair cells use active transport to take in mineral ions (e.g. nitrates) from dilute soil solution',
    ],
    we: {
      problem: 'A cube has sides of 2 cm. Calculate its surface-area-to-volume ratio.',
      steps: [
        { step: 1, instruction: 'Surface area = 6 × side²', working: '6 × 2² = 6 × 4 = 24 cm²' },
        { step: 2, instruction: 'Volume = side³', working: '2³ = 8 cm³' },
        { step: 3, instruction: 'SA:V', working: '24:8 = 3:1' },
      ],
      answer: '3:1',
      commonMistake: 'Forgetting × 6 — there are six identical faces on a cube.',
    },
    glossary: [
      { term: 'Diffusion', definition: 'Net movement of particles from high to low concentration. Passive.' },
      { term: 'Osmosis', definition: 'Net movement of WATER from a dilute to a concentrated solution through a partially permeable membrane.' },
      { term: 'Active transport', definition: 'Movement of particles AGAINST a concentration gradient, using energy from respiration.' },
      { term: 'Partially permeable', definition: 'Membrane that lets some particles pass but not others.' },
      { term: 'Concentration gradient', definition: 'A difference in concentration between two regions.' },
    ],
    rp: {
      title: 'Required Practical 2: Investigating osmosis in potato chips',
      aqaRef: 'AQA Required Practical (Biology) 3',
      aim: 'Investigate the effect of a range of sucrose concentrations on the mass of plant tissue.',
      method: [
        'Cut 5 potato chips of equal length and mass; record initial mass.',
        'Place each chip in a different sucrose concentration (0.0, 0.2, 0.4, 0.6, 1.0 mol/dm³).',
        'Leave for 30 minutes.',
        'Remove chips, blot gently, record final mass.',
        'Calculate % change in mass = (final − initial) / initial × 100.',
        'Plot % change against concentration. Where the curve crosses zero = isotonic point of the potato.',
      ],
      safety: 'Sharp knife — cut on a board. Iodine if used as control = stains skin.',
      tips: [
        'Always blot chips with the same firmness — affects mass.',
        'Use percentage change (not raw mass) — different starting masses then become comparable.',
      ],
    },
    examQ: {
      prompt: 'Explain how root hair cells are adapted to absorb water and mineral ions from the soil. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'Long extensions/projections give a large surface area for absorption',
        'Many mitochondria provide energy / ATP for active transport',
        'Water enters by osmosis (dilute soil → concentrated cell)',
        'Mineral ions enter by active transport AGAINST concentration gradient',
      ],
    },
    practice: {
      warmUp: [
        cQ('b1-7.w1', 'Diffusion is movement from:', ['Low to high', 'High to low', 'Equal to equal', 'Random'], 'High to low', 'Down the gradient', 'Passive', 'Like perfume'),
        tQ('b1-7.w2', 'Movement of water through a partially permeable membrane is ___.', 'osmosis', 'Special case of diffusion', 'Water only', '7 letters'),
      ],
      level1: [
        cQ('b1-7.1', 'Active transport requires:', ['Heat', 'Energy from respiration (ATP)', 'A pressure gradient', 'Sunlight'], 'Energy from respiration (ATP)', 'Against the gradient', 'Cell uses ATP', 'Mitochondria provide it'),
        nQ('b1-7.2', 'A 3 cm cube. SA:V ratio? Give the larger number first as a single integer with no colon.', '2', 'SA = 54, V = 27', '54/27 = 2', 'Ratio 2:1 → answer 2'),
      ],
      level2: [
        cQ('b1-7.3', 'Why do small organisms not need transport systems?', ['They don\'t respire', 'Diffusion alone is fast enough due to large SA:V', 'They store all nutrients in cells', 'They are dormant'], 'Diffusion alone is fast enough due to large SA:V', 'Surface-area : volume', 'Short distance', 'Big SA:V'),
        tQ('b1-7.4', 'Mineral ions enter root hairs against the gradient by ___.', 'active transport', 'Needs energy', 'Two words', 'A_____ T_______'),
      ],
    },
  }),
];

// =============================================================
// B2 — Organisation
// =============================================================
export const b2OrganisationTopics = [
  gt('gcse-b2-1', 'principles-of-organisation', 'Principles of Organisation', {
    pageRef: 'B2.1',
    intro: 'Multicellular organisms are organised in a hierarchy. Cells form tissues (groups of cells with similar structure and function). Tissues form organs (combinations of different tissues working together). Organs form organ systems. Organ systems work together as a whole organism. Examples: muscular tissue + glandular tissue + epithelial tissue → stomach (organ) → digestive system → human.',
    keyPoints: [
      'Hierarchy: cells → tissues → organs → organ systems → organism',
      'Tissue = group of similar cells working together',
      'Organ = several tissues combined to do a job',
      'Organ system = several organs working together',
      'Stomach = muscular (churning) + glandular (enzymes/acid) + epithelial (lining)',
    ],
    glossary: [
      { term: 'Tissue', definition: 'A group of cells with similar structure performing the same function.' },
      { term: 'Organ', definition: 'A combination of tissues working together to do a specific job.' },
      { term: 'Organ system', definition: 'A group of organs working together; e.g. digestive system.' },
      { term: 'Epithelial tissue', definition: 'Lining tissue covering surfaces and lining cavities.' },
    ],
    practice: {
      warmUp: [
        cQ('b2-1.w1', 'Order from smallest to largest:', ['Tissue → cell → organ → system', 'Cell → tissue → organ → system', 'Organ → tissue → cell → system', 'System → organ → tissue → cell'], 'Cell → tissue → organ → system', 'Cells are basic unit', 'Build up', 'Bottom-up'),
      ],
      level1: [
        tQ('b2-1.1', 'Group of cells with similar structure and function is a ___.', 'tissue', 'Below organ', '6 letters', 'Like muscle, epithelium'),
      ],
      level2: [
        cQ('b2-1.2', 'The stomach is an organ because…', ['It has only one tissue', 'Several tissue types work together', 'It has cells', 'It is part of a system'], 'Several tissue types work together', 'Definition of organ', 'Mix of tissues', 'Multiple types'),
      ],
    },
  }),

  gt('gcse-b2-2', 'digestion-enzymes', 'Digestion & Enzymes', {
    pageRef: 'B2.2',
    intro: 'Digestion breaks large insoluble food molecules into small soluble ones that can be absorbed into the blood. Three groups of digestive enzymes: amylase (breaks starch → maltose), protease (proteins → amino acids), lipase (lipids → fatty acids + glycerol). Enzymes are biological catalysts — large protein molecules with a specific active site (the "lock and key" model). Each enzyme has an optimum temperature (~37 °C in humans) and pH. Beyond optimum, enzymes denature: the active site changes shape and the substrate no longer fits.',
    keyPoints: [
      'Carbohydrases (e.g. amylase) → simple sugars',
      'Proteases (e.g. pepsin in stomach, trypsin in small intestine) → amino acids',
      'Lipases → fatty acids + glycerol',
      'Lock-and-key: enzyme active site is complementary in shape to substrate',
      'Optimum temperature for human enzymes ≈ 37 °C; optimum pH varies (pepsin pH 2; amylase pH 7)',
      'Bile (made in liver, stored in gall bladder) emulsifies fats AND neutralises stomach acid',
    ],
    we: {
      problem: 'Explain why heating an enzyme to 80 °C stops it working.',
      steps: [
        { step: 1, instruction: 'Identify what happens to enzyme', working: 'High temperature breaks the bonds holding the protein shape' },
        { step: 2, instruction: 'Effect on active site', working: 'Active site changes shape — no longer complementary to substrate' },
        { step: 3, instruction: 'Conclude', working: 'Substrate cannot bind, so the enzyme is denatured (irreversible)' },
      ],
      answer: 'High temperature denatures the enzyme: bonds break, active site changes shape so substrate cannot bind.',
    },
    glossary: [
      { term: 'Enzyme', definition: 'Biological catalyst (a protein) that speeds up a reaction without being used up.' },
      { term: 'Active site', definition: 'Region of the enzyme where the substrate binds.' },
      { term: 'Substrate', definition: 'The molecule the enzyme acts on.' },
      { term: 'Denatured', definition: 'Active site has changed shape so the enzyme no longer works. Often irreversible.' },
      { term: 'Bile', definition: 'Alkaline liquid that emulsifies fats and neutralises stomach acid.' },
    ],
    examQ: {
      prompt: 'A student investigates the activity of amylase at different pH values. Describe how pH affects enzyme activity and explain why. (6 marks)',
      maxMarks: 6,
      markSchemePoints: [
        'There is an optimum pH at which the enzyme works fastest',
        'Above and below the optimum, activity decreases',
        'Far from optimum the enzyme denatures',
        'Denaturation means the active site changes shape',
        'Substrate no longer fits the active site / no longer complementary',
        'No enzyme-substrate complex forms / no products made',
      ],
    },
    examinerInsights: {
      whyMarksLost: [
        'Saying enzymes are "killed" — they are PROTEINS, so the correct word is denatured.',
        'Forgetting to mention the optimum — students jump straight to "high pH stops activity".',
        'Mixing up substrate and product (the substrate doesn\'t fit, not the product).',
        'Giving only one symptom of denaturation when 6 marks demand multiple linked points.',
      ],
      whatExaminersWant: [
        'A clear shape: introduce optimum → describe trend either side → explain mechanism.',
        'Use of the term "complementary" when describing active site fit (1 mark).',
        'Linkage: shape change → no enzyme-substrate complex → no product. Each link = 1 mark.',
      ],
      commonTraps: [
        'Confusing pH with temperature — same idea (denaturation) but different cause.',
        'Saying the active site disappears — it changes SHAPE, not vanishes.',
        'Stating "enzyme stops working" without explaining WHY (no marks for tautology).',
      ],
      gradeNineMove: 'Use the linker phrase "this means…" between every cause and effect. Examiners are explicitly trained to look for explanatory chains, not just lists of facts.',
    },
    practice: {
      warmUp: [
        cQ('b2-2.w1', 'Which enzyme breaks down starch?', ['Lipase', 'Protease', 'Amylase', 'Catalase'], 'Amylase', 'Amylo = starch', 'Salivary glands make it', 'A_______'),
        tQ('b2-2.w2', 'Bile emulsifies ___.', 'fats', '3 letters', 'Lipids broken into droplets', 'F___'),
      ],
      level1: [
        cQ('b2-2.1', 'Optimum pH of pepsin in the stomach?', ['pH 2', 'pH 7', 'pH 9', 'pH 11'], 'pH 2', 'Stomach is acidic', 'Below 7', 'HCl present'),
        tQ('b2-2.2', 'Lipase digests lipids to fatty acids and ___.', 'glycerol', '8 letters', 'G_______', 'Sweet alcohol'),
      ],
      level2: [
        cQ('b2-2.3', 'Why is the lock-and-key model useful?', ['Active site is rigid', 'Enzyme is destroyed each time', 'Substrate fits the active site only', 'Enzymes work at any temperature'], 'Substrate fits the active site only', 'Specificity', 'Complementary shape', 'One key per lock'),
      ],
    },
  }),

  gt('gcse-b2-3', 'food-tests', 'Food Tests (RP3)', {
    pageRef: 'B2.3',
    intro: 'Four standard food tests identify the major nutrient groups: starch, sugar, protein, lipid. Each gives a colour change with a specific reagent. These are the most testable practical procedures at GCSE — examiners want both the colour change and the reagent name.',
    keyPoints: [
      'Starch + iodine → blue-black',
      'Reducing sugars + Benedict\'s, heat → brick-red precipitate',
      'Protein + Biuret → purple/lilac',
      'Lipid + ethanol then add water → cloudy white emulsion',
      'Always crush solid food and mix with water before testing',
      'Use a separate test tube for EACH test',
    ],
    glossary: [
      { term: 'Iodine', definition: 'Reagent for starch. Brown-orange → blue-black if starch present.' },
      { term: 'Benedict\'s', definition: 'Blue reagent for reducing sugars. Becomes brick-red when heated with sugar present.' },
      { term: 'Biuret', definition: 'Blue reagent for protein. Goes purple/lilac if protein present.' },
      { term: 'Emulsion', definition: 'Cloudy white suspension of fat droplets in water — positive ethanol-emulsion test.' },
    ],
    rp: {
      title: 'Required Practical 3: Use of qualitative reagents to test for biological molecules',
      aqaRef: 'AQA Required Practical (Biology) 4',
      aim: 'Identify the presence of starch, reducing sugars, protein and lipid in a range of food samples.',
      method: [
        'Crush a solid sample with distilled water in a mortar and pestle.',
        'Filter to obtain a clear solution. Divide into 4 equal portions in test tubes.',
        'STARCH: Add a few drops of iodine. Note any colour change to blue-black.',
        'REDUCING SUGAR: Add Benedict\'s reagent. Heat in water bath at ~70 °C for 5 min. Brick-red = positive.',
        'PROTEIN: Add Biuret solution. Purple/lilac = positive.',
        'LIPID: Add ethanol, shake, then pour into water. Cloudy white emulsion = positive.',
      ],
      safety: 'Iodine stains skin/clothes. Ethanol is flammable — keep away from naked flame. Heated water can cause scalds.',
      tips: [
        'Always include a control (water only) so you know what NEGATIVE looks like.',
        'Benedict\'s requires HEAT — without it the test does not work.',
        'For lipid test, use COLD ethanol straight onto crushed sample.',
      ],
    },
    practice: {
      warmUp: [
        cQ('b2-3.w1', 'Starch + iodine →', ['Brick-red', 'Blue-black', 'Purple', 'Cloudy white'], 'Blue-black', 'Iodine = starch', 'Dark colour', 'Colour change'),
        cQ('b2-3.w2', 'Brick-red precipitate with heat = ', ['Protein', 'Lipid', 'Reducing sugar', 'Starch'], 'Reducing sugar', 'Benedict\'s test', 'Heat needed', 'Sweet'),
      ],
      level1: [
        tQ('b2-3.1', 'Reagent that turns purple with protein is ___.', 'biuret', '6 letters', 'B______', 'Two e\'s'),
        cQ('b2-3.2', 'Cloudy white emulsion in water test = ', ['Starch', 'Protein', 'Lipid', 'Sugar'], 'Lipid', 'Ethanol-water', 'Fat in water', 'L____'),
      ],
      level2: [
        cQ('b2-3.3', 'Why do you need a control in food tests?', ['To compare colour to a known negative', 'To save reagents', 'To speed up the test', 'No reason'], 'To compare colour to a known negative', 'Reference', 'Tells you negative result', 'Scientific method'),
      ],
    },
  }),

  gt('gcse-b2-4', 'heart-circulation', 'The Heart & Circulation', {
    pageRef: 'B2.4',
    intro: 'Humans have a double circulatory system: blood passes through the heart twice per cycle. Right side pumps deoxygenated blood to lungs (pulmonary circuit); left side pumps oxygenated blood around the body (systemic circuit). Four chambers: right and left atria (top) receive blood; right and left ventricles (bottom) pump it out. The left ventricle has the thickest muscle wall — it pumps blood the furthest. Coronary arteries supply the heart muscle itself with oxygen.',
    keyPoints: [
      'Double circulatory system: lung circuit + body circuit',
      'Right atrium ← deoxygenated blood from body (vena cava)',
      'Right ventricle → lungs via pulmonary artery',
      'Left atrium ← oxygenated blood from lungs (pulmonary vein)',
      'Left ventricle → body via aorta (thickest wall — pumps furthest)',
      'Valves prevent backflow; lub-dub heart sound = valves closing',
      'Pacemaker cells in right atrium control natural heart rate; artificial pacemakers can correct rhythm',
    ],
    glossary: [
      { term: 'Atrium', definition: 'Upper heart chamber that receives blood.' },
      { term: 'Ventricle', definition: 'Lower heart chamber that pumps blood out.' },
      { term: 'Aorta', definition: 'Largest artery; carries oxygenated blood from left ventricle to body.' },
      { term: 'Pulmonary artery', definition: 'Carries deoxygenated blood from right ventricle to lungs (the only artery carrying deoxygenated blood).' },
      { term: 'Vena cava', definition: 'Large vein returning deoxygenated blood from the body to the right atrium.' },
      { term: 'Pacemaker', definition: 'Group of cells in the right atrium that control the heart rate.' },
    ],
    practice: {
      warmUp: [
        cQ('b2-4.w1', 'Which chamber pumps blood to the body?', ['Right atrium', 'Left atrium', 'Right ventricle', 'Left ventricle'], 'Left ventricle', 'Thickest wall', 'Pumps furthest', 'Bottom-left'),
        tQ('b2-4.w2', 'Vessel carrying deoxygenated blood to lungs is the ___ artery.', 'pulmonary', 'P________', 'To lungs', 'Pulmo = lung'),
      ],
      level1: [
        cQ('b2-4.1', 'Why is the left ventricle wall thicker than the right?', ['It holds more blood', 'It pumps blood further (around the body)', 'It is a backup', 'It receives blood'], 'It pumps blood further (around the body)', 'Body circuit longer', 'Higher pressure needed', 'Aorta route'),
      ],
      level2: [
        tQ('b2-4.2', 'Vessels supplying heart muscle with oxygen are the ___ arteries.', 'coronary', 'C_______', 'Around the heart', 'Block = heart attack'),
      ],
    },
  }),

  gt('gcse-b2-5', 'blood-blood-vessels', 'Blood & Blood Vessels', {
    pageRef: 'B2.5',
    intro: 'Blood has 4 components: red blood cells (carry oxygen via haemoglobin), white blood cells (immune defence), platelets (blood clotting) and plasma (carries CO₂, hormones, glucose, urea, dissolved waste). Three types of vessel: arteries (thick wall + small lumen, carry blood AT HIGH PRESSURE AWAY from heart); veins (thin wall + large lumen + valves, carry blood at low pressure back); capillaries (one-cell-thick wall, exchange site).',
    keyPoints: [
      'Red blood cells: biconcave, no nucleus, packed with haemoglobin',
      'White blood cells: have nucleus; phagocytes engulf pathogens; lymphocytes make antibodies',
      'Platelets: cell fragments; clot blood at wounds',
      'Plasma: yellow fluid carrying everything dissolved',
      'Arteries: thick muscular wall, narrow lumen, NO valves (except aortic+pulmonary at heart)',
      'Veins: thin wall, wide lumen, valves to prevent backflow',
      'Capillaries: one-cell-thick wall, sites of substance exchange (O₂, CO₂, glucose)',
    ],
    glossary: [
      { term: 'Plasma', definition: 'Liquid part of blood; transports dissolved substances.' },
      { term: 'Haemoglobin', definition: 'Iron-containing protein in red blood cells that binds oxygen.' },
      { term: 'Phagocyte', definition: 'White blood cell that engulfs and digests pathogens.' },
      { term: 'Capillary', definition: 'Tiny vessel with a one-cell-thick wall where exchange happens.' },
      { term: 'Lumen', definition: 'The hollow centre of a tube — the space blood flows through.' },
    ],
    practice: {
      warmUp: [
        cQ('b2-5.w1', 'Which carry oxygen?', ['White blood cells', 'Red blood cells', 'Platelets', 'Plasma'], 'Red blood cells', 'Haemoglobin', 'Biconcave shape', 'Red colour'),
        tQ('b2-5.w2', 'Vessels with valves preventing backflow are ___.', 'veins', '5 letters', 'Low pressure', 'V____'),
      ],
      level1: [
        cQ('b2-5.1', 'Capillary walls are one cell thick because…', ['They are weak', 'Short diffusion distance', 'Cheap to make', 'Aesthetic'], 'Short diffusion distance', 'For exchange', 'Faster gas swap', 'Tissue-level adaptation'),
      ],
      level2: [
        tQ('b2-5.2', 'Cell fragments that help blood clot are ___.', 'platelets', 'P_______', 'Wound repair', 'Tiny'),
      ],
    },
  }),

  gt('gcse-b2-6', 'cardiovascular-disease', 'Cardiovascular Disease', {
    pageRef: 'B2.6',
    intro: 'Coronary heart disease (CHD) happens when fatty deposits (atheroma) build up in coronary arteries, narrowing the lumen and reducing blood flow to heart muscle. This can cause angina (chest pain) or a heart attack (myocardial infarction) if blood flow is fully blocked. Treatments: stents (mesh tubes that hold the artery open), statins (drugs that lower blood cholesterol), bypass surgery, heart transplant. Lifestyle factors: smoking, high-fat diet, lack of exercise, alcohol all increase risk.',
    keyPoints: [
      'CHD: fatty plaques (atheroma) narrow coronary arteries',
      'Angina = chest pain on exertion; heart attack = full blockage',
      'Stents: hold arteries open (mechanical, immediate relief)',
      'Statins: drugs that lower LDL cholesterol over time (risks: muscle pain, slow effect)',
      'Faulty valves can be replaced with mechanical or biological valves',
      'Donor heart transplant for end-stage failure (rejection risk)',
      'Lifestyle risk factors: smoking, high-fat/salt diet, lack of exercise, alcohol, obesity',
    ],
    glossary: [
      { term: 'CHD', definition: 'Coronary heart disease — narrowing of coronary arteries.' },
      { term: 'Stent', definition: 'A mesh tube inserted into a narrowed artery to hold it open.' },
      { term: 'Statin', definition: 'A drug that lowers LDL cholesterol in the blood, slowing artery narrowing.' },
      { term: 'Atheroma', definition: 'Fatty plaque on the inside wall of an artery.' },
    ],
    practice: {
      warmUp: [
        cQ('b2-6.w1', 'A stent is used to:', ['Lower cholesterol', 'Hold a narrowed artery open', 'Replace a valve', 'Pump blood'], 'Hold a narrowed artery open', 'Mechanical fix', 'Mesh tube', 'Inserted into vessel'),
      ],
      level1: [
        tQ('b2-6.1', 'Drugs that reduce blood cholesterol are ___.', 'statins', '7 letters', 'S______', 'Reduce LDL'),
        cQ('b2-6.2', 'Which is NOT a CHD risk factor?', ['Smoking', 'High exercise', 'High-fat diet', 'Obesity'], 'High exercise', 'Lifestyle factors', 'Exercise is protective', 'Reduces risk'),
      ],
      level2: [
        cQ('b2-6.3', 'Pros of stents vs statins?', ['Slower acting', 'Immediate effect', 'Cheaper', 'No surgery needed'], 'Immediate effect', 'Mechanical', 'Surgical', 'Quick blood-flow restore'),
      ],
    },
  }),

  gt('gcse-b2-7', 'health-cancer-disease', 'Health, Disease & Cancer', {
    pageRef: 'B2.7',
    intro: 'Health = state of physical and mental wellbeing. Two main disease types: communicable (caused by pathogens, can spread — e.g. measles, TB) and non-communicable (cannot spread — e.g. CHD, type 2 diabetes, most cancers). Different diseases interact: HPV virus → cervical cancer; immune-suppressed patients more vulnerable to infection. Cancer = uncontrolled cell division. Benign tumours stay where they form; malignant tumours invade neighbouring tissues and metastasise via the blood.',
    keyPoints: [
      'Communicable disease: spread by pathogens (bacteria, virus, fungus, protist)',
      'Non-communicable disease: not spread; CHD, diabetes, most cancers',
      'Benign tumour: contained, slow-growing, rarely fatal if removed',
      'Malignant tumour: invades surrounding tissue + metastasises through blood = cancer',
      'Lifestyle risks for cancer: smoking (lung), UV (skin), obesity, alcohol',
      'Genetic risks for cancer: BRCA gene mutations → breast, ovarian',
    ],
    glossary: [
      { term: 'Communicable disease', definition: 'Disease caused by a pathogen that can be passed from one organism to another.' },
      { term: 'Non-communicable', definition: 'A disease that cannot spread between organisms.' },
      { term: 'Benign tumour', definition: 'A tumour that does not invade other tissues; usually not fatal.' },
      { term: 'Malignant tumour', definition: 'A cancerous tumour that invades surrounding tissue and spreads to other parts of the body.' },
      { term: 'Metastasis', definition: 'Spread of cancer cells through the blood/lymph to form secondary tumours.' },
    ],
    practice: {
      warmUp: [
        cQ('b2-7.w1', 'Cancer is caused by:', ['Pathogens always', 'Uncontrolled cell division', 'Lifestyle only', 'Genes only'], 'Uncontrolled cell division', 'Mitosis gone wrong', 'Tumour growth', 'Cells multiplying'),
      ],
      level1: [
        tQ('b2-7.1', 'A tumour that spreads to other parts of the body is called ___.', 'malignant', 'M________', 'Cancerous', 'Opposite of benign'),
      ],
      level2: [
        cQ('b2-7.2', 'Which is non-communicable?', ['Influenza', 'Tuberculosis', 'CHD', 'Measles'], 'CHD', 'Heart disease', 'Lifestyle-linked', 'No pathogen'),
      ],
    },
  }),
];

// =============================================================
// B3 — Infection & Response
// =============================================================
export const b3InfectionResponseTopics = [
  gt('gcse-b3-1', 'communicable-diseases-pathogens', 'Communicable Diseases & Pathogens', {
    pageRef: 'B3.1',
    intro: 'Pathogens are microorganisms that cause communicable disease. Four types: bacteria (release toxins), viruses (live and reproduce inside cells, killing them), protists (often spread by vectors like mosquitoes) and fungi. Pathogens spread by direct contact, by air (droplets in coughs/sneezes), by water (drinking contaminated) and via vectors. Spread is reduced by hygiene, isolating infected people, vaccination, vector control and destroying contaminated food.',
    keyPoints: [
      'Bacteria: divide rapidly inside body; cause disease by toxins (e.g. Salmonella food poisoning)',
      'Viruses: reproduce inside host cells, then burst the cell — destroys tissues',
      'Protists: often single-celled; many use vectors (e.g. malaria via mosquito)',
      'Fungi: e.g. rose black spot, athlete\'s foot',
      'Spread reduction: handwashing, isolation, vaccination, vector destruction',
    ],
    glossary: [
      { term: 'Pathogen', definition: 'A microorganism that causes communicable disease.' },
      { term: 'Vector', definition: 'An organism that transfers a pathogen to a host (e.g. mosquito for malaria).' },
      { term: 'Toxin', definition: 'A poison made by some pathogens (e.g. by bacteria) that damages tissues.' },
    ],
    practice: {
      warmUp: [
        cQ('b3-1.w1', 'A virus reproduces:', ['Inside host cells', 'In soil', 'On plant leaves', 'In bloodstream only'], 'Inside host cells', 'Needs cell machinery', 'Then bursts cell', 'Obligate parasite'),
      ],
      level1: [
        tQ('b3-1.1', 'A mosquito spreading malaria is acting as a ___.', 'vector', 'V______', 'Carrier organism', '6 letters'),
      ],
      level2: [
        cQ('b3-1.2', 'Best way to reduce spread of cholera (water-borne)?', ['Vaccinate insects', 'Clean drinking water and sewage systems', 'Antibiotics for everyone', 'Wear masks'], 'Clean drinking water and sewage systems', 'Water route', 'Sanitation', 'Public health'),
      ],
    },
  }),

  gt('gcse-b3-2', 'bacterial-diseases', 'Bacterial Diseases', {
    pageRef: 'B3.2',
    intro: 'Two named bacterial diseases: Salmonella food poisoning (in raw poultry; symptoms fever, abdominal cramps, vomiting, diarrhoea — caused by toxins) and Gonorrhoea (sexually transmitted; symptoms thick yellow/green discharge, pain on urination — historically treated with penicillin but resistant strains have emerged).',
    keyPoints: [
      'Salmonella: food poisoning; vaccinated UK poultry to reduce spread; cook food thoroughly',
      'Gonorrhoea: STI caused by Neisseria gonorrhoeae; treated with antibiotics; barrier contraception prevents spread',
      'Antibiotic resistance is rising — finish full courses, do not over-prescribe',
    ],
    glossary: [
      { term: 'Salmonella', definition: 'Bacterium causing food poisoning; common in raw poultry and eggs.' },
      { term: 'Gonorrhoea', definition: 'A sexually-transmitted bacterial infection.' },
      { term: 'Antibiotic resistance', definition: 'When bacteria evolve to survive antibiotic treatment.' },
    ],
    practice: {
      warmUp: [
        tQ('b3-2.w1', 'Bacterium causing food poisoning from raw chicken: ___.', 'salmonella', 'S_________', 'Genus name', '10 letters'),
      ],
      level1: [
        cQ('b3-2.1', 'How is Gonorrhoea spread?', ['Air', 'Water', 'Sexual contact', 'Insect vector'], 'Sexual contact', 'STI', 'Direct contact', 'Body fluids'),
      ],
      level2: [
        cQ('b3-2.2', 'Why finish a full antibiotic course?', ['Tastes better', 'Reduces bacteria fully and resistance development', 'It\'s cheaper', 'No reason'], 'Reduces bacteria fully and resistance development', 'Resistance prevention', 'Public health', 'Evolutionary reason'),
      ],
    },
  }),

  gt('gcse-b3-3', 'viral-fungal-diseases', 'Viral & Fungal Diseases', {
    pageRef: 'B3.3',
    intro: 'Three named viral diseases: Measles (spread by droplets in air; fever + red rash; can be fatal; vaccine in MMR); HIV (sexual contact / shared needles; attacks immune lymphocytes; causes AIDS unless treated with antiretrovirals); Tobacco mosaic virus or TMV (a plant virus causing mosaic-pattern leaves and reduced photosynthesis). Fungal example: Rose black spot (causes purple/black spots on leaves; reduces photosynthesis; treated with fungicides or by removing affected leaves).',
    keyPoints: [
      'Measles: airborne droplets; fever + rash; MMR vaccine prevents',
      'HIV: sexual fluids/blood; attacks T-helper cells; AIDS = late stage; antiretrovirals control',
      'TMV: plant virus; mosaic discoloured leaves; reduces photosynthesis and yield',
      'Rose black spot: fungal; black/purple leaf spots; treated with fungicide or leaf removal',
    ],
    glossary: [
      { term: 'HIV', definition: 'Human Immunodeficiency Virus; attacks lymphocytes; can lead to AIDS.' },
      { term: 'AIDS', definition: 'Late-stage HIV; immune system unable to fight infection.' },
      { term: 'TMV', definition: 'Tobacco Mosaic Virus; causes mosaic discolouration in plant leaves.' },
      { term: 'Fungicide', definition: 'A chemical that kills fungi.' },
    ],
    practice: {
      warmUp: [
        cQ('b3-3.w1', 'How is measles spread?', ['Water', 'Sexual contact', 'Droplets in air', 'Soil'], 'Droplets in air', 'Coughs/sneezes', 'Airborne', 'Respiratory'),
      ],
      level1: [
        tQ('b3-3.1', 'A virus that attacks plant leaves causing yellow patterns: ___.', 'tmv', '3 letters', 'Tobacco ___ virus', 'Acronym'),
      ],
      level2: [
        cQ('b3-3.2', 'Why is HIV NOT cured by antibiotics?', ['HIV is a virus, antibiotics target bacteria', 'It is too small', 'It hides in muscle', 'Antibiotics are too weak'], 'HIV is a virus, antibiotics target bacteria', 'Different drug class', 'Mechanism', 'Common misconception'),
      ],
    },
  }),

  gt('gcse-b3-4', 'human-defence-immune', 'Human Defences & Immune System', {
    pageRef: 'B3.4',
    intro: 'Three lines of physical/chemical defence: skin (barrier), mucus and cilia in airways (trap and sweep out pathogens), stomach acid (kills swallowed pathogens), tears (contain antibacterial enzymes). White blood cells provide active immunity: phagocytes engulf pathogens; lymphocytes produce specific antibodies that bind to antigens on a pathogen, marking it for destruction; some lymphocytes also produce antitoxins. Memory cells remain after first exposure — second exposure produces a faster, stronger response.',
    keyPoints: [
      'Skin = physical barrier; mucus + cilia trap pathogens',
      'Stomach acid kills pathogens swallowed in food',
      'Phagocytes: engulf and digest pathogens',
      'Lymphocytes: produce antibodies that bind specifically to antigens',
      'Antitoxins: neutralise toxins',
      'Memory cells produce a faster secondary response — basis of immunity',
    ],
    glossary: [
      { term: 'Antigen', definition: 'A molecule on the surface of a pathogen recognised as foreign.' },
      { term: 'Antibody', definition: 'A protein made by lymphocytes that binds to a specific antigen.' },
      { term: 'Phagocyte', definition: 'White blood cell that engulfs and digests pathogens.' },
      { term: 'Lymphocyte', definition: 'White blood cell that makes antibodies and antitoxins.' },
    ],
    practice: {
      warmUp: [
        cQ('b3-4.w1', 'Which white blood cell engulfs pathogens?', ['Lymphocyte', 'Phagocyte', 'Platelet', 'Erythrocyte'], 'Phagocyte', 'Phago = eat', 'Engulfs', 'Like Pac-Man'),
      ],
      level1: [
        tQ('b3-4.1', 'Proteins made by lymphocytes that bind to antigens are ___.', 'antibodies', 'A_________', 'Specific to one antigen', 'Y-shaped'),
      ],
      level2: [
        cQ('b3-4.2', 'Memory cells exist to:', ['Slow down response', 'Give faster, stronger response on second exposure', 'Eat pathogens', 'Make blood'], 'Give faster, stronger response on second exposure', 'Immunity', 'Secondary response', 'Vaccination basis'),
      ],
    },
  }),

  gt('gcse-b3-5', 'vaccination-antibiotics', 'Vaccination & Antibiotics', {
    pageRef: 'B3.5',
    intro: 'Vaccination introduces small amounts of dead or inactive pathogen so the immune system makes memory cells without the disease. If a real infection arrives later, lymphocytes produce the right antibodies quickly. Herd immunity: when enough of a population is vaccinated, the disease cannot spread effectively. Antibiotics (e.g. penicillin) kill bacteria — they have NO effect on viruses. Painkillers reduce symptoms but do not kill the pathogen. Antibiotic-resistant strains (e.g. MRSA) develop through natural selection — finishing courses + targeted prescriptions slow this down.',
    keyPoints: [
      'Vaccine = dead/inactive pathogen → memory cells made',
      'On real infection: rapid antibody production → person doesn\'t become ill',
      'Herd immunity protects unvaccinated when proportion vaccinated is high',
      'Antibiotics kill bacteria only (NOT viruses)',
      'Antibiotic resistance via natural selection — MRSA',
      'Painkillers ease symptoms but do not cure',
    ],
    glossary: [
      { term: 'Vaccine', definition: 'Preparation of dead/inactive pathogen used to stimulate immunity.' },
      { term: 'Herd immunity', definition: 'Protection of a whole population when enough individuals are vaccinated.' },
      { term: 'Antibiotic', definition: 'A drug that kills bacteria (no effect on viruses).' },
      { term: 'MRSA', definition: 'Methicillin-resistant Staphylococcus aureus — antibiotic-resistant bacterial strain.' },
    ],
    examQ: {
      prompt: 'Explain how vaccination protects a person from a disease, even though they have never had it. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'Vaccine contains a dead or inactive form of the pathogen with the same antigens',
        'Lymphocytes (white blood cells) produce specific antibodies in response',
        'Memory cells are made and remain in the body',
        'On future exposure, memory cells produce antibodies quickly so the person does not become ill',
      ],
    },
    practice: {
      warmUp: [
        cQ('b3-5.w1', 'Antibiotics work against:', ['Viruses', 'Bacteria', 'Fungi only', 'Protists'], 'Bacteria', 'Antibacterial', 'Penicillin example', 'Not viruses'),
      ],
      level1: [
        tQ('b3-5.1', 'Cells produced after vaccination giving long-term protection are ___ cells.', 'memory', '6 letters', 'Long-lived lymphocytes', 'M_____'),
      ],
      level2: [
        cQ('b3-5.2', 'Why is herd immunity important?', ['Protects vaccinated only', 'Protects unvaccinated by stopping spread', 'Replaces hygiene', 'Cures viruses'], 'Protects unvaccinated by stopping spread', 'Population effect', 'Group protection', 'Reduces R-number'),
      ],
    },
  }),

  gt('gcse-b3-6', 'drug-development', 'Drug Development', {
    pageRef: 'B3.6',
    intro: 'New drugs go through 3 main stages: (1) Preclinical — tested on cells, tissues and live animals for toxicity. (2) Clinical trials Phase 1: tested on small group of healthy volunteers at very low doses to check safety. (3) Clinical trials Phase 2/3: tested on patients with the disease, including double-blind placebo trials (neither doctor nor patient knows who has placebo). Tests check toxicity, efficacy and dose. Many fail. Drugs originally came from plants (digitalis from foxglove; aspirin from willow bark) and microorganisms (penicillin from Penicillium mould).',
    keyPoints: [
      'Preclinical: test on cells, tissues, live animals',
      'Clinical Phase 1: small healthy human volunteer group, low dose — safety',
      'Clinical Phase 2/3: patient groups + placebo double-blind trials — efficacy + dose',
      'Tests check: toxicity (harm), efficacy (does it work), optimal dose',
      'Original sources: plants, microorganisms — penicillin discovered by Fleming (Penicillium mould)',
      'Most drugs fail somewhere in this pipeline',
    ],
    glossary: [
      { term: 'Preclinical trial', definition: 'Testing a drug on cells, tissues and live animals before human use.' },
      { term: 'Placebo', definition: 'A "fake" treatment used as a control to test whether the real drug has effect.' },
      { term: 'Double-blind trial', definition: 'Trial where neither patient nor doctor knows who has placebo or real drug.' },
      { term: 'Efficacy', definition: 'How well a treatment works.' },
    ],
    practice: {
      warmUp: [
        cQ('b3-6.w1', 'A double-blind trial means:', ['Patients are blindfolded', 'Neither patient nor doctor knows who has placebo', 'Drugs tested at night', 'Animals tested twice'], 'Neither patient nor doctor knows who has placebo', 'Removes bias', 'Both blinded', 'Gold standard'),
      ],
      level1: [
        tQ('b3-6.1', 'A "fake" treatment used as a control is a ___.', 'placebo', 'P______', 'Sugar pill', '7 letters'),
      ],
      level2: [
        cQ('b3-6.2', 'Penicillin was discovered from a:', ['Plant', 'Bacterium', 'Mould (fungus)', 'Animal'], 'Mould (fungus)', 'Fleming 1928', 'Penicillium', 'Fungal genus'),
      ],
    },
  }),

  gt('gcse-b3-7', 'monoclonal-antibodies', 'Monoclonal Antibodies', {
    pageRef: 'B3.7',
    tier: 'higher',
    intro: 'Monoclonal antibodies (mAbs) are identical antibodies all made from clones of a single B-lymphocyte. Production: a mouse is injected with the antigen → spleen lymphocytes (which make antibodies but don\'t divide well) are fused with myeloma tumour cells (which divide rapidly but don\'t make antibodies) → hybridoma cells that BOTH divide AND make a single specific antibody. Uses: pregnancy tests (detect hCG hormone), diagnosing diseases (detect specific molecules), treating cancer (deliver toxic drug ONLY to cancer cells). Side-effects more severe than expected.',
    keyPoints: [
      'Made from clones of one B-lymphocyte → identical antibodies',
      'Process: mouse antigen → lymphocytes + myeloma cell → hybridoma → mAbs',
      'Specific to ONE antigen',
      'Uses: pregnancy tests (hCG), disease diagnosis, targeted cancer treatment',
      'Cancer treatment: mAbs carry a drug or radioactive tracer to cancer cells specifically',
      'More side effects than expected — limited use compared to original hopes',
    ],
    glossary: [
      { term: 'Monoclonal', definition: 'Coming from a single clone of cells.' },
      { term: 'Hybridoma', definition: 'A cell formed by fusing a B-lymphocyte with a myeloma tumour cell.' },
      { term: 'hCG', definition: 'Human chorionic gonadotropin — pregnancy hormone detected in pregnancy tests.' },
    ],
    practice: {
      warmUp: [
        cQ('b3-7.w1', 'mAbs are made from:', ['Many different cells', 'A single B-cell clone', 'Phagocytes', 'Memory cells'], 'A single B-cell clone', 'Mono = one', 'Identical antibodies', 'Cloned'),
      ],
      level1: [
        tQ('b3-7.1', 'Pregnancy tests detect the hormone ___.', 'hcg', '3 letters', 'Acronym', 'Made by embryo'),
      ],
      level2: [
        cQ('b3-7.2', 'In cancer treatment, mAbs:', ['Replace the immune system', 'Carry drugs specifically to cancer cells', 'Vaccinate against cancer', 'Cure all cancers'], 'Carry drugs specifically to cancer cells', 'Targeted therapy', 'Specific delivery', 'Reduces healthy-tissue damage'),
      ],
    },
  }),
];

// =============================================================
// B4 — Bioenergetics
// =============================================================
export const b4BioenergeticsTopics = [
  gt('gcse-b4-1', 'photosynthesis-reaction', 'Photosynthesis Reaction & Limiting Factors', {
    pageRef: 'B4.1',
    intro: 'Photosynthesis is an endothermic reaction that takes place in chloroplasts: carbon dioxide + water → glucose + oxygen, using light energy absorbed by chlorophyll. The four limiting factors are light intensity, CO₂ concentration, temperature and (less importantly) chlorophyll level. Increasing one only helps until something else becomes limiting. Inverse-square law: doubling distance from light → ¼ light intensity. Greenhouses use artificial light, CO₂ enrichment and heating to optimise yield.',
    keyPoints: [
      'CO₂ + H₂O → glucose (C₆H₁₂O₆) + O₂ (light + chlorophyll)',
      'Endothermic: takes IN energy from light',
      'Limiting factors: light intensity, CO₂ concentration, temperature',
      'Inverse-square law for light: I ∝ 1/d²',
      'Greenhouses optimise all 3 factors simultaneously',
    ],
    we: {
      problem: 'A lamp 0.5 m from a plant gives a light intensity of 100 units. What is the intensity at 2.0 m?',
      steps: [
        { step: 1, instruction: 'Inverse-square: I × d² = constant', working: '100 × 0.5² = 100 × 0.25 = 25' },
        { step: 2, instruction: 'At 2.0 m: I = 25 ÷ d²', working: '25 ÷ 4 = 6.25' },
      ],
      answer: '6.25 units',
      commonMistake: 'Treating light as 1/d (linear) instead of 1/d² (inverse-square).',
    },
    glossary: [
      { term: 'Photosynthesis', definition: 'The process plants use to make glucose using light energy.' },
      { term: 'Chlorophyll', definition: 'Green pigment in chloroplasts that absorbs light energy.' },
      { term: 'Limiting factor', definition: 'A factor that limits the rate of a reaction at any given time.' },
      { term: 'Endothermic', definition: 'A reaction that takes IN energy.' },
    ],
    practice: {
      warmUp: [
        cQ('b4-1.w1', 'Photosynthesis is:', ['Exothermic', 'Endothermic', 'Both', 'Neither'], 'Endothermic', 'Takes IN light energy', 'Energy absorbed', 'Endo = in'),
      ],
      level1: [
        nQ('b4-1.1', 'Light at 1 m = 100 units. Intensity at 2 m? (inverse-square)', '25', '100 ÷ 2²', '100 ÷ 4', '2 digits'),
      ],
      level2: [
        cQ('b4-1.2', 'Why does adding CO₂ stop helping above a certain level?', ['CO₂ becomes toxic', 'Light or temperature becomes limiting', 'Plants close stomata', 'Photosynthesis stops'], 'Light or temperature becomes limiting', 'Limiting-factor concept', 'Another factor in deficit', 'Bottleneck'),
      ],
    },
  }),

  gt('gcse-b4-2', 'investigating-photosynthesis', 'Investigating Photosynthesis (RP4)', {
    pageRef: 'B4.2',
    intro: 'A standard rate-of-photosynthesis experiment uses pondweed (Cabomba or Elodea) submerged in sodium hydrogen carbonate solution (CO₂ source). A lamp at varying distances changes light intensity. Bubbles of oxygen released per minute = the rate of photosynthesis. Plotting bubble count vs 1/d² gives a straight line at low intensities, then a plateau as another factor (CO₂ or temperature) becomes limiting.',
    keyPoints: [
      'Pondweed in NaHCO₃ solution = constant CO₂ supply',
      'Lamp distance varied; bubbles counted per minute',
      'Use a heat sink (water bath) to control temperature',
      'Light intensity ∝ 1/d² (inverse-square)',
      'Initial linear region → plateau as another factor limits',
    ],
    rp: {
      title: 'Required Practical 4: Effect of light intensity on photosynthesis',
      aqaRef: 'AQA Required Practical (Biology) 6',
      aim: 'Investigate the effect of light intensity on the rate of photosynthesis using an aquatic plant.',
      method: [
        'Set up pondweed in a boiling tube containing 0.2% NaHCO₃ solution.',
        'Place the boiling tube inside a water bath (heat sink) at constant temperature.',
        'Position a lamp at 10 cm from the pondweed.',
        'Count oxygen bubbles released per minute. Take 3 repeats.',
        'Move lamp to 20, 30, 40, 50 cm. Repeat counts.',
        'Plot bubbles/min against light intensity (1/d²).',
      ],
      safety: 'Lamps get hot — beware burns. Glassware may break.',
      tips: [
        'Always include a control of room light only.',
        'Use a heat sink so you isolate light as the variable, not temperature.',
      ],
    },
    glossary: [
      { term: 'Pondweed', definition: 'Aquatic plant (e.g. Cabomba, Elodea) used to study photosynthesis.' },
      { term: 'NaHCO₃', definition: 'Sodium hydrogen carbonate — provides dissolved CO₂ in water.' },
    ],
    practice: {
      warmUp: [
        cQ('b4-2.w1', 'Why use NaHCO₃ in pondweed experiments?', ['Adds light', 'Provides CO₂', 'Prevents heating', 'Kills algae'], 'Provides CO₂', 'CO₂ source', 'Dissolved CO₂', 'For photosynthesis'),
      ],
      level1: [
        tQ('b4-2.1', 'Counting ___ per minute measures rate of photosynthesis in pondweed.', 'bubbles', '7 letters', 'Oxygen produced', 'B______'),
      ],
      level2: [
        cQ('b4-2.2', 'Why use a water bath as a heat sink?', ['Controls light', 'Keeps temperature constant when lamp is close', 'Increases CO₂', 'Adds nutrients'], 'Keeps temperature constant when lamp is close', 'Isolates variable', 'Lamp heats water otherwise', 'Control variable'),
      ],
    },
  }),

  gt('gcse-b4-3', 'uses-of-glucose', 'Uses of Glucose in Plants', {
    pageRef: 'B4.3',
    intro: 'Glucose made in photosynthesis is used by plants in 5 main ways: (1) Respiration — releases energy. (2) Stored as starch — insoluble, doesn\'t affect water potential. (3) Converted to lipids/oils — stored in seeds. (4) Used to make cellulose — strengthens cell walls. (5) Combined with nitrate ions to make amino acids → proteins. So although photosynthesis only directly makes glucose, the plant uses it as the building block for everything.',
    keyPoints: [
      '1. Respiration → ATP for cell processes',
      '2. Storage as starch (insoluble polysaccharide)',
      '3. Conversion to oils/lipids — energy-rich seed store',
      '4. Cellulose for cell walls',
      '5. Combine with nitrates → amino acids → proteins',
    ],
    glossary: [
      { term: 'Starch', definition: 'Plant storage carbohydrate; insoluble polymer of glucose.' },
      { term: 'Cellulose', definition: 'Polymer of glucose forming plant cell walls.' },
      { term: 'Nitrate ions', definition: 'NO₃⁻ — taken in by roots; used with glucose to make amino acids.' },
    ],
    practice: {
      warmUp: [
        cQ('b4-3.w1', 'Plants store glucose as ___.', ['Glycogen', 'Starch', 'Cellulose', 'Protein'], 'Starch', 'Plant store', 'Insoluble', 'Iodine test'),
      ],
      level1: [
        tQ('b4-3.1', 'Glucose + nitrates → ___ acids.', 'amino', '5 letters', 'Build proteins', 'A____'),
      ],
      level2: [
        cQ('b4-3.2', 'Why is starch a better store than glucose?', ['Sweeter', 'Insoluble — no osmotic effect', 'Smaller', 'Easier to make'], 'Insoluble — no osmotic effect', 'Doesn\'t change water potential', 'Glucose would draw water', 'Storage form'),
      ],
    },
  }),

  gt('gcse-b4-4', 'aerobic-respiration', 'Aerobic Respiration & Metabolism', {
    pageRef: 'B4.4',
    intro: 'Aerobic respiration is an exothermic reaction in mitochondria: glucose + oxygen → carbon dioxide + water (+ energy). The energy is used for muscle contraction, building larger molecules from smaller ones (e.g. starch from glucose, proteins from amino acids), and keeping warm. Metabolism is the sum of all reactions in a cell or body — from breaking glucose down to building proteins, lipids and DNA.',
    keyPoints: [
      'C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O (+ energy)',
      'In mitochondria; exothermic',
      'Energy used for: movement, biosynthesis, keeping warm (mammals/birds)',
      'Metabolism = all chemical reactions in a cell or organism',
      'Anabolic = building (amino acids → proteins, glucose → starch); catabolic = breaking (digestion)',
    ],
    glossary: [
      { term: 'Aerobic respiration', definition: 'Respiration with oxygen; releases more energy than anaerobic.' },
      { term: 'Metabolism', definition: 'All chemical reactions taking place in an organism.' },
      { term: 'Mitochondrion', definition: 'Organelle where aerobic respiration occurs.' },
    ],
    practice: {
      warmUp: [
        cQ('b4-4.w1', 'Aerobic respiration is:', ['Endothermic', 'Exothermic', 'Both', 'Neither'], 'Exothermic', 'Releases energy', 'Heat output', 'Exo = out'),
        tQ('b4-4.w2', 'Where does aerobic respiration occur?', 'mitochondria', 'M___________', 'Powerhouse organelle', 'Plural'),
      ],
      level1: [
        cQ('b4-4.1', 'Products of aerobic respiration:', ['Glucose + O₂', 'CO₂ + H₂O + energy', 'Lactic acid', 'Ethanol + CO₂'], 'CO₂ + H₂O + energy', 'Inverse of photosynthesis', 'With oxygen', 'Most energy'),
      ],
      level2: [
        tQ('b4-4.2', 'All chemical reactions in an organism collectively are called ___.', 'metabolism', 'M_________', 'Greek metabole', '10 letters'),
      ],
    },
  }),

  gt('gcse-b4-5', 'anaerobic-respiration', 'Anaerobic Respiration & Exercise', {
    pageRef: 'B4.5',
    intro: 'When oxygen runs short (e.g. during hard exercise), cells switch to anaerobic respiration: glucose → lactic acid (in animal muscle), or glucose → ethanol + CO₂ (in yeast = fermentation). Anaerobic releases far LESS energy per glucose because oxidation is incomplete. Lactic acid build-up causes muscle fatigue. Excess post-exercise oxygen consumption (oxygen debt) breaks lactic acid down in the liver after exercise. Yeast fermentation is used in bread (CO₂ rises dough) and brewing (ethanol).',
    keyPoints: [
      'Animal muscle: glucose → lactic acid (no oxygen)',
      'Yeast: glucose → ethanol + CO₂ (fermentation)',
      'Anaerobic releases far less energy than aerobic',
      'Lactic acid causes muscle fatigue and pain',
      'Oxygen debt = oxygen needed AFTER exercise to break down lactic acid (in liver)',
      'Heart rate, breathing rate and breath volume all rise during exercise to deliver O₂ + remove CO₂',
    ],
    examQ: {
      prompt: 'Explain why a sprinter\'s breathing rate stays high for several minutes after a 100 m race finishes. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'During the race anaerobic respiration produces lactic acid in the muscles',
        'Oxygen is needed to break down the lactic acid (oxygen debt)',
        'Lactic acid is transported to the liver and broken down to CO₂ + H₂O',
        'Until oxygen debt is paid off, breathing stays high',
      ],
    },
    glossary: [
      { term: 'Anaerobic respiration', definition: 'Respiration without oxygen; releases less energy.' },
      { term: 'Lactic acid', definition: 'Waste product of anaerobic respiration in animal muscle.' },
      { term: 'Oxygen debt', definition: 'Extra oxygen needed after exercise to break down lactic acid.' },
      { term: 'Fermentation', definition: 'Anaerobic respiration in yeast producing ethanol + CO₂.' },
    ],
    practice: {
      warmUp: [
        cQ('b4-5.w1', 'Anaerobic respiration in muscle produces:', ['Ethanol', 'CO₂ + water', 'Lactic acid', 'Glucose'], 'Lactic acid', 'Animal pathway', 'Causes fatigue', 'Acidic'),
      ],
      level1: [
        tQ('b4-5.1', 'Yeast anaerobic respiration produces ethanol and ___.', 'carbon dioxide', 'CO_2', 'Two words', 'Bubbles in beer'),
        cQ('b4-5.2', 'Anaerobic vs aerobic energy yield?', ['Same', 'Anaerobic > aerobic', 'Aerobic >> anaerobic', 'Anaerobic = 0'], 'Aerobic >> anaerobic', 'Aerobic complete oxidation', 'Much more ATP', 'Aerobic wins'),
      ],
      level2: [
        cQ('b4-5.3', 'Lactic acid is broken down in the:', ['Brain', 'Heart', 'Lungs', 'Liver'], 'Liver', 'Detoxification organ', 'Hepatocytes', '5 letters'),
      ],
    },
  }),
];

// =============================================================
// B5 — Homeostasis & Response
// =============================================================
export const b5HomeostasisTopics = [
  gt('gcse-b5-1', 'principles-homeostasis', 'Principles of Homeostasis', {
    pageRef: 'B5.1',
    intro: 'Homeostasis is the regulation of internal conditions to maintain optimal cell function despite changing external/internal conditions. In humans, regulated variables include body temperature (~37 °C), blood glucose (~5 mmol/L) and water balance. Every control system has 3 components: receptors detect a stimulus, a coordination centre (brain, spinal cord, pancreas) processes information, and effectors (muscles or glands) bring about a response. Negative feedback returns conditions to optimum.',
    keyPoints: [
      'Receptors → coordination centre → effectors → response',
      'Negative feedback restores conditions to setpoint',
      'Examples regulated: temperature, blood glucose, water/salt',
      'Coordination by nervous system (fast) + endocrine system (slower, longer-lasting)',
    ],
    glossary: [
      { term: 'Homeostasis', definition: 'Maintenance of constant internal conditions.' },
      { term: 'Receptor', definition: 'Cell that detects a stimulus.' },
      { term: 'Effector', definition: 'Muscle or gland that brings about a response.' },
      { term: 'Negative feedback', definition: 'Response that reverses a change to keep conditions stable.' },
    ],
    practice: {
      warmUp: [
        cQ('b5-1.w1', 'Homeostasis maintains:', ['External temperature', 'Constant internal conditions', 'Skin colour', 'Body shape'], 'Constant internal conditions', 'Internal balance', 'Within set range', 'Definition'),
      ],
      level1: [
        tQ('b5-1.1', 'Cells that detect a stimulus are ___.', 'receptors', '9 letters', 'R________', 'Sensory cells'),
      ],
      level2: [
        cQ('b5-1.2', 'Negative feedback:', ['Amplifies change', 'Reverses change to maintain set point', 'Stops all responses', 'Only affects mood'], 'Reverses change to maintain set point', 'Restores balance', 'Counteracts deviation', 'Stability mechanism'),
      ],
    },
  }),

  gt('gcse-b5-2', 'nervous-system-reflex', 'Nervous System & Reflex Arc', {
    pageRef: 'B5.2',
    intro: 'The nervous system carries fast electrical signals along neurones. Three neurone types: sensory (detect stimulus → CNS), relay (in the CNS, connect sensory to motor), motor (CNS → effector). The CNS is brain + spinal cord. A reflex arc bypasses conscious thought for a fast protective response: stimulus → receptor → sensory neurone → relay neurone (in spinal cord) → motor neurone → effector → response. Synapses (gaps between neurones) use chemical neurotransmitters.',
    keyPoints: [
      'Stimulus → receptor → sensory → relay → motor → effector → response',
      'Reflex arc is involuntary and fast — no brain involvement (just spinal cord)',
      'Synapse uses neurotransmitters; signal becomes chemical then electrical again',
      'CNS = brain + spinal cord',
    ],
    examQ: {
      prompt: 'Describe the path of a reflex action when a person\'s hand touches a hot object. (5 marks)',
      maxMarks: 5,
      markSchemePoints: [
        'Heat receptors in the skin detect the stimulus',
        'Signal passes along sensory neurone',
        'Through synapse to relay neurone in spinal cord',
        'Then through synapse to motor neurone',
        'Motor neurone to muscle (effector); muscle contracts and pulls hand away',
      ],
    },
    glossary: [
      { term: 'Neurone', definition: 'A nerve cell.' },
      { term: 'Synapse', definition: 'Gap between two neurones; uses chemical neurotransmitters.' },
      { term: 'Reflex arc', definition: 'Pathway of an automatic, rapid response.' },
      { term: 'CNS', definition: 'Central nervous system: brain + spinal cord.' },
    ],
    practice: {
      warmUp: [
        cQ('b5-2.w1', 'Order in a reflex arc:', ['Receptor → motor → relay → sensory', 'Receptor → sensory → relay → motor → effector', 'Effector → motor → relay → sensory', 'Receptor → relay → sensory → motor'], 'Receptor → sensory → relay → motor → effector', 'In to out', 'Standard order', 'Sensory before motor'),
      ],
      level1: [
        tQ('b5-2.1', 'Gap between two neurones is called a ___.', 'synapse', 'S______', 'Chemical bridge', '7 letters'),
      ],
      level2: [
        cQ('b5-2.2', 'Why does the reflex arc bypass the brain?', ['Brain too slow for protective response', 'Brain doesn\'t work', 'Spinal cord is bigger', 'Saves energy'], 'Brain too slow for protective response', 'Speed matters', 'Protective', 'Survival'),
      ],
    },
  }),

  gt('gcse-b5-3', 'reaction-time-rp', 'Reaction Time (RP5)', {
    pageRef: 'B5.3',
    intro: 'Reaction time = time from a stimulus appearing to the response starting. Typical 0.1-0.3 s for a simple reaction in humans. The ruler-drop test: subject holds thumb and forefinger 5 cm apart; tester drops a 30 cm ruler between them; subject catches it as soon as possible; distance fallen converts to time using s = ½gt². Caffeine, fatigue, age and distractions affect reaction time.',
    keyPoints: [
      'Reaction time = time from stimulus to start of response',
      'Ruler drop: distance fallen → time via s = ½gt² (g ≈ 10 m/s²)',
      'Repeat to find a mean; controls: same fingers, same height drop, same observer',
      'Caffeine speeds reactions; alcohol/fatigue slows them',
    ],
    rp: {
      title: 'Required Practical 5: Plan and carry out an investigation into the effect of a factor on human reaction time',
      aqaRef: 'AQA Required Practical (Biology) 7',
      aim: 'Investigate the effect of caffeine (or another factor) on the reaction time of a subject.',
      method: [
        'Subject sits with arm on a table, dominant hand off the edge.',
        'Tester holds a 30 cm ruler vertically with 0 cm between subject\'s thumb and finger.',
        'Tester drops the ruler with no warning; subject catches it as soon as possible.',
        'Record the distance fallen (cm) on the ruler at thumb position.',
        'Repeat 5 times, calculate mean. Convert to time using t = √(2s/g).',
        'Repeat 30 minutes after subject drinks a caffeinated drink. Compare means.',
      ],
      safety: 'Do not use any substance the subject cannot legally consume; check for caffeine allergies.',
      tips: [
        'Drop without warning so subject cannot anticipate.',
        'Always use the same fingers and same starting position.',
        'Take 5+ readings and use the mean to reduce random error.',
      ],
    },
    glossary: [
      { term: 'Reaction time', definition: 'Time from stimulus to start of voluntary response.' },
    ],
    practice: {
      warmUp: [
        cQ('b5-3.w1', 'A typical reaction time is:', ['1-3 s', '0.1-0.3 s', '0.001 s', '10 s'], '0.1-0.3 s', 'Subsecond', 'Hundreds of ms', 'Fast'),
      ],
      level1: [
        cQ('b5-3.1', 'Why repeat the ruler-drop several times?', ['To make subject tired', 'Reduce random error / find mean', 'Use up the ruler', 'No reason'], 'Reduce random error / find mean', 'Statistical reliability', 'Repeats average out variation', 'Standard practice'),
      ],
      level2: [
        cQ('b5-3.2', 'Caffeine on reaction time:', ['Slows it', 'Speeds it', 'No effect', 'Stops it'], 'Speeds it', 'Stimulant', 'Faster signals', 'Quickens reactions'),
      ],
    },
  }),

  gt('gcse-b5-4', 'the-brain', 'The Brain', {
    pageRef: 'B5.4',
    tier: 'higher',
    intro: 'The brain is the most complex structure in the body. Three major regions: cerebral cortex (outer wrinkly layer; consciousness, intelligence, memory, language), cerebellum (back; muscle coordination, balance) and medulla (top of brain stem; controls unconscious vitals — breathing, heart rate). Studying the brain is hard: lesion studies (damage from stroke); electrical stimulation; MRI scans. Treating brain disease/injury is risky because the brain has limited repair ability and surgery can damage healthy tissue.',
    keyPoints: [
      'Cerebral cortex: thinking, memory, language, consciousness',
      'Cerebellum: balance and motor coordination',
      'Medulla: unconscious functions — breathing, heart rate',
      'Studied via: lesion (damage) studies, electrical stimulation, MRI scans',
      'Treatment difficult: limited repair, easy to damage healthy tissue',
    ],
    glossary: [
      { term: 'Cerebral cortex', definition: 'Outer brain layer responsible for higher-level functions.' },
      { term: 'Cerebellum', definition: 'Back-of-brain region responsible for balance and coordination.' },
      { term: 'Medulla', definition: 'Brain-stem region controlling unconscious vital processes.' },
      { term: 'MRI', definition: 'Magnetic Resonance Imaging — non-invasive brain scan.' },
    ],
    practice: {
      warmUp: [
        cQ('b5-4.w1', 'Which region controls breathing and heart rate?', ['Cerebellum', 'Cerebral cortex', 'Medulla', 'Pituitary'], 'Medulla', 'Brain stem', 'Unconscious', 'Vital signs'),
      ],
      level1: [
        tQ('b5-4.1', 'Outer wrinkly layer responsible for thought is the cerebral ___.', 'cortex', '6 letters', 'C_____', 'Latin "bark"'),
      ],
      level2: [
        cQ('b5-4.2', 'Why is brain surgery risky?', ['Always fatal', 'Can damage healthy tissue with limited repair ability', 'Brain is too small', 'No anaesthesia'], 'Can damage healthy tissue with limited repair ability', 'Neurones don\'t replace easily', 'Collateral damage', 'Plasticity is limited'),
      ],
    },
  }),

  gt('gcse-b5-5', 'the-eye', 'The Eye', {
    pageRef: 'B5.5',
    intro: 'The eye is a sense organ — receptors are in the retina. Light passes through cornea (first refraction), pupil (hole in iris), lens (fine focus), and lands on the retina. Iris controls pupil diameter via reflexes (bright light → constrict pupil; dim light → dilate). Lens shape changes via ciliary muscles + suspensory ligaments — accommodation. For close objects: ciliary muscles CONTRACT, ligaments slacken, lens thickens. For distant: ciliary muscles RELAX, ligaments tighten, lens thins. Defects: myopia (short-sight, fixed by concave lens), hyperopia (long-sight, fixed by convex lens).',
    keyPoints: [
      'Cornea — outer transparent layer; main refraction',
      'Iris — coloured ring; controls pupil size via antagonistic muscles',
      'Lens — fine focusing via ciliary muscles + suspensory ligaments',
      'Retina — has rods (low light) and cones (colour); receptors',
      'Optic nerve carries signals to brain',
      'Accommodation: close = thick lens; distant = thin lens',
      'Myopia: corrected by concave lens; Hyperopia: convex lens',
    ],
    glossary: [
      { term: 'Cornea', definition: 'Transparent front of the eye; main light refraction.' },
      { term: 'Iris', definition: 'Coloured muscular ring controlling pupil diameter.' },
      { term: 'Lens', definition: 'Transparent disc that fine-focuses light onto the retina.' },
      { term: 'Retina', definition: 'Layer of light-sensitive cells (rods + cones).' },
      { term: 'Accommodation', definition: 'Lens-shape change to focus on near or distant objects.' },
    ],
    practice: {
      warmUp: [
        cQ('b5-5.w1', 'Which structure is light-sensitive?', ['Cornea', 'Iris', 'Retina', 'Lens'], 'Retina', 'Has rods + cones', 'Photoreceptors', 'Back of eye'),
      ],
      level1: [
        cQ('b5-5.1', 'Looking at a near object, ciliary muscles:', ['Relax', 'Contract', 'Stop', 'Dissolve'], 'Contract', 'Lens becomes thicker', 'Close work', 'Round lens'),
      ],
      level2: [
        cQ('b5-5.2', 'Myopia is corrected with a:', ['Convex lens', 'Concave lens', 'Tinted lens', 'No lens'], 'Concave lens', 'Diverging lens', 'Short-sight fix', 'Hollow shape'),
      ],
    },
  }),

  gt('gcse-b5-6', 'hormones-endocrine', 'Hormones & Endocrine System', {
    pageRef: 'B5.6',
    intro: 'The endocrine system uses chemical messengers (hormones) released from glands directly into the blood, where they travel to target organs. Slower than nerves but longer-lasting. Pituitary gland (in the brain) is the master gland — controls many others by releasing FSH, LH, TSH, ADH. Other key glands: thyroid (thyroxine — metabolic rate), adrenal (adrenaline — fight or flight), pancreas (insulin/glucagon — blood glucose), ovaries (oestrogen, progesterone), testes (testosterone).',
    keyPoints: [
      'Hormones: chemical messengers in blood',
      'Pituitary = master gland (FSH, LH, TSH, ADH)',
      'Thyroid → thyroxine (metabolism)',
      'Adrenal → adrenaline (fight/flight)',
      'Pancreas → insulin / glucagon (blood glucose)',
      'Ovaries / testes → sex hormones',
      'Hormonal response is slower but longer-lasting than nervous',
    ],
    glossary: [
      { term: 'Hormone', definition: 'Chemical messenger released into the blood from an endocrine gland.' },
      { term: 'Endocrine gland', definition: 'Gland that releases hormones into the blood (no duct).' },
      { term: 'Pituitary', definition: 'Master gland at base of brain; releases hormones controlling other glands.' },
    ],
    practice: {
      warmUp: [
        cQ('b5-6.w1', 'Hormones are carried by:', ['Nerves', 'Blood', 'Lymph only', 'Air'], 'Blood', 'Bloodstream', 'Liquid transport', 'Cardiovascular'),
      ],
      level1: [
        tQ('b5-6.1', 'The "master gland" of the endocrine system is the ___.', 'pituitary', 'P________', 'Below brain', 'Bean-sized'),
      ],
      level2: [
        cQ('b5-6.2', 'Adrenaline causes:', ['Sleep', 'Fight or flight response', 'Digestion to start', 'Lactation'], 'Fight or flight response', 'Stress hormone', 'Heart rate up', 'Adrenal medulla'),
      ],
    },
  }),

  gt('gcse-b5-7', 'blood-glucose-diabetes', 'Blood Glucose & Diabetes', {
    pageRef: 'B5.7',
    intro: 'Pancreas monitors and controls blood glucose. After a meal, glucose rises → pancreas releases insulin → cells (especially liver and muscle) take in glucose; liver converts excess glucose to glycogen for storage. If glucose is low, pancreas releases glucagon (Higher) → liver breaks glycogen back into glucose. Type 1 diabetes: pancreas fails to make insulin (autoimmune; treated with insulin injections). Type 2: cells stop responding to insulin properly (linked to obesity; managed by diet, exercise, sometimes drugs).',
    keyPoints: [
      'Insulin → cells absorb glucose; liver converts to glycogen',
      'Glucagon (Higher) → liver releases glucose',
      'Type 1: pancreas fails to make insulin; needs injections',
      'Type 2: cells become insulin-resistant; manage by diet + exercise',
      'Risk factors for type 2: obesity, age, family history',
    ],
    examQ: {
      prompt: 'Explain how blood glucose concentration is controlled in a healthy person after a meal high in carbohydrates. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'Pancreas detects increased blood glucose',
        'Pancreas secretes insulin into the blood',
        'Insulin causes cells (especially liver and muscle) to take up glucose',
        'Liver converts excess glucose to glycogen for storage (returning glucose to normal range)',
      ],
    },
    glossary: [
      { term: 'Insulin', definition: 'Hormone released by the pancreas that lowers blood glucose.' },
      { term: 'Glucagon', definition: 'Hormone released by the pancreas that raises blood glucose by mobilising glycogen.' },
      { term: 'Glycogen', definition: 'Glucose store in liver and muscle.' },
      { term: 'Type 1 diabetes', definition: 'Pancreas does not produce enough insulin.' },
    ],
    practice: {
      warmUp: [
        cQ('b5-7.w1', 'Insulin is made by the:', ['Liver', 'Pancreas', 'Kidney', 'Adrenal gland'], 'Pancreas', 'Endocrine + exocrine', 'Islets of Langerhans', 'Behind stomach'),
      ],
      level1: [
        tQ('b5-7.1', 'Liver storage form of glucose is ___.', 'glycogen', '8 letters', 'Animal starch', 'G_______'),
      ],
      level2: [
        cQ('b5-7.2', 'Type 1 diabetes is treated with:', ['Diet only', 'Insulin injections', 'Antibiotics', 'Glucose tablets only'], 'Insulin injections', 'Pancreas can\'t make insulin', 'Replacement', 'Daily'),
      ],
    },
  }),

  gt('gcse-b5-8', 'reproduction-hormones', 'Reproduction Hormones & Contraception', {
    pageRef: 'B5.8',
    intro: 'Puberty is triggered by sex hormones from the gonads: testosterone (testes — main male hormone) and oestrogen (ovaries — main female hormone). The menstrual cycle (~28 days) is controlled by 4 hormones: FSH (causes egg to mature), oestrogen (builds uterus lining, inhibits FSH), LH (triggers ovulation around day 14), progesterone (maintains lining; high level inhibits FSH and LH). Hormonal contraception (pill, patch) uses oestrogen + progesterone to prevent ovulation. Non-hormonal: condoms (barrier; also prevent STIs), copper IUD, sterilisation.',
    keyPoints: [
      'Male: testosterone (testes); Female: oestrogen + progesterone (ovaries)',
      'FSH (pituitary) → egg matures',
      'Oestrogen → uterus lining thickens',
      'LH → ovulation (day 14)',
      'Progesterone → maintains lining; if no fertilisation falls → menstruation',
      'Hormonal contraceptives: pill, patch, implant, injection',
      'Barrier: condoms — only method that also prevents STIs',
      'IVF: FSH/LH given to mature multiple eggs; collected, fertilised in vitro, embryos transferred',
    ],
    glossary: [
      { term: 'FSH', definition: 'Follicle Stimulating Hormone — causes eggs to mature.' },
      { term: 'LH', definition: 'Luteinising Hormone — triggers ovulation.' },
      { term: 'Oestrogen', definition: 'Female sex hormone; thickens uterus lining.' },
      { term: 'Progesterone', definition: 'Hormone maintaining uterus lining during pregnancy.' },
      { term: 'IVF', definition: 'In Vitro Fertilisation — eggs fertilised outside the body.' },
    ],
    practice: {
      warmUp: [
        cQ('b5-8.w1', 'Which hormone triggers ovulation?', ['FSH', 'LH', 'Oestrogen', 'Progesterone'], 'LH', 'Day 14', 'Surge', 'Luteinising'),
      ],
      level1: [
        tQ('b5-8.1', 'Female sex hormone produced by ovaries: ___.', 'oestrogen', 'O________', 'Builds lining', '9 letters'),
      ],
      level2: [
        cQ('b5-8.2', 'Which contraceptive method also prevents STIs?', ['Pill', 'Implant', 'IUD', 'Condom'], 'Condom', 'Barrier method', 'Physical barrier', 'Latex'),
      ],
    },
  }),
];

// =============================================================
// B6 — Inheritance, Variation & Evolution
// =============================================================
export const b6InheritanceTopics = [
  gt('gcse-b6-1', 'sexual-asexual-meiosis', 'Sexual vs Asexual Reproduction & Meiosis', {
    pageRef: 'B6.1',
    intro: 'Asexual reproduction: one parent, mitosis only, offspring are genetically identical clones. Examples: bacteria binary fission, strawberry runners. Sexual reproduction: two parents, gametes form by MEIOSIS (special division producing 4 genetically different haploid cells from one diploid cell), gametes fuse at fertilisation, offspring are genetically unique. Meiosis halves chromosome number (in humans 46 → 23 in each gamete) so fertilisation restores the diploid 46.',
    keyPoints: [
      'Asexual: 1 parent, mitosis, identical clones',
      'Sexual: 2 parents, gametes fuse, genetic variation',
      'Meiosis: 1 diploid cell → 4 haploid gametes',
      'Humans: 46 (diploid) → 23 (haploid) per gamete; fertilisation restores 46',
      'Variation comes from: independent assortment, crossing over, random fertilisation',
    ],
    glossary: [
      { term: 'Meiosis', definition: 'Cell division producing 4 genetically different haploid gametes.' },
      { term: 'Haploid', definition: 'Has ONE set of chromosomes (gametes).' },
      { term: 'Diploid', definition: 'Has TWO sets of chromosomes (body cells).' },
      { term: 'Gamete', definition: 'Sex cell — egg or sperm.' },
    ],
    practice: {
      warmUp: [
        cQ('b6-1.w1', 'Meiosis produces:', ['2 identical diploid', '4 different haploid', '4 identical haploid', '2 different diploid'], '4 different haploid', 'Halves chromosomes', '4 unique gametes', 'Sex cells'),
      ],
      level1: [
        tQ('b6-1.1', 'A cell with ONE set of chromosomes is called ___.', 'haploid', '7 letters', 'Half', 'H______'),
      ],
      level2: [
        cQ('b6-1.2', 'Strawberry plants spreading by runners is:', ['Sexual', 'Asexual', 'Both', 'Neither'], 'Asexual', 'One parent', 'Mitosis only', 'Identical clones'),
      ],
    },
  }),

  gt('gcse-b6-2', 'dna-genome', 'DNA & The Genome', {
    pageRef: 'B6.2',
    intro: 'DNA is a polymer of nucleotides forming a double helix. The 4 bases A-T, C-G pair across the helix (complementary base pairing). A gene is a section of DNA coding for a particular sequence of amino acids in a protein. The genome is the entire genetic material of an organism — the human genome has about 20,000-25,000 genes. Knowing the genome helps in: tracing migration, identifying inherited diseases, developing personalised medicine.',
    keyPoints: [
      'DNA: double helix, 4 bases A-T C-G (complementary)',
      'Gene = section of DNA coding for a protein',
      'Genome = ALL the DNA of an organism',
      'Human Genome Project completed 2003',
      'Uses: ancestry, disease tracing, personalised medicine, screening',
    ],
    glossary: [
      { term: 'DNA', definition: 'Deoxyribonucleic acid — the genetic material.' },
      { term: 'Gene', definition: 'A section of DNA coding for a particular protein.' },
      { term: 'Genome', definition: 'The entire genetic material of an organism.' },
      { term: 'Base pair', definition: 'Two complementary bases joined across the DNA helix (A-T, C-G).' },
    ],
    practice: {
      warmUp: [
        cQ('b6-2.w1', 'DNA shape is:', ['Single helix', 'Double helix', 'Straight chain', 'Sphere'], 'Double helix', 'Two strands', 'Twisted ladder', 'Watson + Crick model'),
      ],
      level1: [
        tQ('b6-2.1', 'A pairs with ___ in DNA.', 't', '1 letter', 'Thymine', 'A↔T'),
      ],
      level2: [
        cQ('b6-2.2', 'Best definition of "genome":', ['One gene', 'All the DNA of an organism', 'A chromosome', 'A protein'], 'All the DNA of an organism', 'Complete set', 'Everything genetic', 'Sum of all genes + non-coding'),
      ],
    },
  }),

  gt('gcse-b6-3', 'genetic-inheritance-punnett', 'Genetic Inheritance & Punnett Squares', {
    pageRef: 'B6.3',
    intro: 'Most characteristics are controlled by multiple genes, but some single-gene traits show simple Mendelian inheritance. Alleles are different versions of a gene. Dominant alleles (capital letter) are expressed even with one copy; recessive (lowercase) need two copies to show. Genotype = the alleles present (e.g. Bb); phenotype = the observable feature. Punnett squares predict ratios of offspring.',
    keyPoints: [
      'Alleles = different versions of a gene',
      'Dominant: expressed with 1 or 2 copies (capital, e.g. B)',
      'Recessive: only expressed with 2 copies (lowercase, e.g. b)',
      'Homozygous = same alleles (BB or bb); Heterozygous = different (Bb)',
      'Punnett square: parents\' alleles → predicted offspring ratios',
    ],
    we: {
      problem: 'Both parents are heterozygous (Bb) for brown eyes. Predict the offspring genotype and phenotype ratios. (b is recessive blue.)',
      steps: [
        { step: 1, instruction: 'Set up a Punnett: parents Bb × Bb', working: 'B and b from each side' },
        { step: 2, instruction: 'Genotype ratio', working: '1 BB : 2 Bb : 1 bb' },
        { step: 3, instruction: 'Phenotype ratio', working: '3 brown (BB + Bb) : 1 blue (bb)' },
      ],
      answer: 'Genotype 1:2:1 (BB:Bb:bb); Phenotype 3:1 (brown:blue)',
      commonMistake: 'Forgetting that BB and Bb both show the dominant phenotype.',
    },
    examQ: {
      prompt: 'Cystic fibrosis is caused by a recessive allele (f). Two healthy parents have a child with cystic fibrosis. State the parents\' genotypes and use a Punnett square to predict the probability that their next child will have cystic fibrosis. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'Both parents must be heterozygous Ff (carriers)',
        'Punnett square: Ff × Ff → 1 FF : 2 Ff : 1 ff',
        '1 in 4 = 25% chance of ff (cystic fibrosis)',
        'Each child is independent — same probability every pregnancy',
      ],
    },
    glossary: [
      { term: 'Allele', definition: 'A variant form of a gene.' },
      { term: 'Dominant allele', definition: 'An allele expressed in the phenotype with one or two copies.' },
      { term: 'Recessive allele', definition: 'An allele only expressed when two copies are present.' },
      { term: 'Genotype', definition: 'The alleles an organism has.' },
      { term: 'Phenotype', definition: 'The observable physical features.' },
      { term: 'Heterozygous', definition: 'Two different alleles for a gene (e.g. Bb).' },
    ],
    practice: {
      warmUp: [
        cQ('b6-3.w1', 'Genotype "Bb" is:', ['Homozygous', 'Heterozygous', 'Hemizygous', 'Aneuploid'], 'Heterozygous', 'Two different', 'Hetero = different', 'Mixed alleles'),
      ],
      level1: [
        nQ('b6-3.1', 'Bb × Bb → what % of offspring are bb? (number, no % sign)', '25', '1/4', '25%', 'Punnett'),
      ],
      level2: [
        cQ('b6-3.2', 'Two heterozygous parents (Bb × Bb). Phenotype ratio?', ['1:1', '3:1', '9:3:3:1', '1:2:1'], '3:1', 'Dominant : recessive', 'Standard monohybrid', '3 dominant phenotype'),
      ],
      level3: [
        nQ('b6-3.3', 'BB × bb. % of offspring heterozygous?', '100', 'All Bb', '100%', '3 digits'),
      ],
    },
  }),

  gt('gcse-b6-4', 'sex-determination-disorders', 'Sex Determination & Inherited Disorders', {
    pageRef: 'B6.4',
    intro: 'In humans, sex is determined by chromosomes 23: XX = female, XY = male. The Y chromosome carries the SRY gene that triggers male development. Each parent passes one sex chromosome — mum always gives X, dad gives X or Y. Some inherited disorders: cystic fibrosis (recessive — thick mucus in lungs), polydactyly (dominant — extra fingers/toes), sickle cell disease (recessive — abnormal haemoglobin). Embryo screening for these disorders before IVF transfer raises ethical questions.',
    keyPoints: [
      'XX = female, XY = male; Y carries SRY → male development',
      'Mum always X; dad X or Y; sex 50:50',
      'Cystic fibrosis: recessive (need ff)',
      'Polydactyly: dominant (need just one P)',
      'Embryo screening + selection raises ethical concerns',
    ],
    glossary: [
      { term: 'Sex chromosomes', definition: 'Pair 23: XX (female) or XY (male) in humans.' },
      { term: 'Cystic fibrosis', definition: 'Recessive genetic disorder; thick mucus in airways and digestive tract.' },
      { term: 'Polydactyly', definition: 'Dominant genetic disorder; extra fingers or toes.' },
    ],
    practice: {
      warmUp: [
        cQ('b6-4.w1', 'Female chromosomes are:', ['XY', 'XX', 'YY', 'X'], 'XX', 'Two X', 'No Y', 'Female karyotype'),
        nQ('b6-4.w2', 'Probability a baby is male? %', '50', '1/2', 'XY half the time', 'Two digits'),
      ],
      level1: [
        cQ('b6-4.1', 'Cystic fibrosis is caused by a:', ['Dominant allele', 'Recessive allele', 'X-linked allele', 'Mitochondrial mutation'], 'Recessive allele', 'Need two copies', 'Carriers can be healthy', 'Both parents must carry'),
      ],
      level2: [
        cQ('b6-4.2', 'Polydactyly is caused by a:', ['Dominant allele', 'Recessive allele', 'No allele', 'Bacterium'], 'Dominant allele', 'Just one copy needed', 'Often visible in family tree', 'PP or Pp'),
      ],
    },
  }),

  gt('gcse-b6-5', 'variation-mutation', 'Variation & Mutation', {
    pageRef: 'B6.5',
    intro: 'Variation between individuals can be genetic (alleles inherited), environmental (e.g. accent, scar), or both (height — genes set range, nutrition decides position within it). All variation in alleles ultimately comes from MUTATION — random, rare changes to DNA. Most mutations have no effect (silent), some change protein function slightly, very few create a new beneficial allele that natural selection can act on. Mutations happen all the time during DNA replication; rate increases with mutagens (radiation, certain chemicals).',
    keyPoints: [
      'Variation: genetic, environmental, or both',
      'Genetic variation arises from mutation',
      'Most mutations: silent (no effect)',
      'Some: change protein function (often harmful)',
      'Rarely: produce a new beneficial allele → raw material for evolution',
      'Mutagens: ionising radiation, UV, certain chemicals (tar in tobacco)',
    ],
    glossary: [
      { term: 'Mutation', definition: 'Random change in the DNA base sequence.' },
      { term: 'Mutagen', definition: 'Agent that increases the rate of mutation (e.g. UV, X-rays, tar).' },
    ],
    practice: {
      warmUp: [
        cQ('b6-5.w1', 'Genetic variation ultimately comes from:', ['Diet', 'Exercise', 'Mutation', 'Climate'], 'Mutation', 'Source of new alleles', 'Random change in DNA', 'Raw material for evolution'),
      ],
      level1: [
        cQ('b6-5.1', 'Most mutations:', ['Are beneficial', 'Have no effect on phenotype', 'Cause cancer', 'Make new species'], 'Have no effect on phenotype', 'Silent changes', 'Mostly neutral', 'Common case'),
      ],
      level2: [
        tQ('b6-5.2', 'Agents that increase mutation rate are called ___.', 'mutagens', '8 letters', 'M_______', 'Causes of mutation'),
      ],
    },
  }),

  gt('gcse-b6-6', 'natural-selection-evolution', 'Evolution by Natural Selection', {
    pageRef: 'B6.6',
    intro: 'Darwin\'s theory: (1) Individuals in a species show variation. (2) More offspring are produced than survive. (3) Best-adapted individuals are more likely to survive and reproduce. (4) They pass on advantageous alleles. (5) Over time, allele frequencies shift; the population evolves. Two famous examples: peppered moth (industrial soot favoured dark moths) and antibiotic-resistant bacteria (resistant strains survive antibiotic exposure and multiply). Evidence: fossils, anatomy comparisons, molecular DNA evidence.',
    keyPoints: [
      'Variation → over-reproduction → survival of fittest → inheritance of advantageous alleles',
      'Allele frequencies change over generations = evolution',
      'Antibiotic resistance: resistant bacteria survive treatment, multiply',
      'Peppered moth: dark form favoured during Industrial Revolution',
      'Evidence: fossils, anatomy, DNA',
    ],
    examQ: {
      prompt: 'A doctor warns patients to always finish the full course of antibiotics. Explain how stopping early can lead to antibiotic-resistant bacteria spreading. (5 marks)',
      maxMarks: 5,
      markSchemePoints: [
        'Bacteria show genetic variation (caused by mutation)',
        'Some bacteria have alleles that give resistance to the antibiotic',
        'Stopping early leaves the most resistant bacteria alive',
        'These survivors reproduce, passing on the resistance allele',
        'Resistant bacteria can spread to other people; antibiotic becomes less effective',
      ],
    },
    glossary: [
      { term: 'Natural selection', definition: 'Process by which the best-adapted individuals are most likely to survive, reproduce and pass on alleles.' },
      { term: 'Evolution', definition: 'Change in allele frequencies over many generations.' },
      { term: 'Antibiotic resistance', definition: 'When bacteria evolve to survive antibiotic treatment.' },
      { term: 'Fossil', definition: 'Preserved evidence of an organism that lived long ago.' },
    ],
    practice: {
      warmUp: [
        cQ('b6-6.w1', 'Best-adapted individuals are MORE likely to:', ['Die early', 'Survive and reproduce', 'Mutate', 'Migrate'], 'Survive and reproduce', 'Differential success', 'Survival of the fittest', 'Pass on alleles'),
      ],
      level1: [
        tQ('b6-6.1', 'Change in allele frequencies over generations is called ___.', 'evolution', '9 letters', 'E________', 'Darwinian process'),
      ],
      level2: [
        cQ('b6-6.2', 'Why does finishing an antibiotic course matter?', ['Tastier', 'Kills resistant bacteria before they spread', 'Cheaper', 'Reduces side effects'], 'Kills resistant bacteria before they spread', 'Public health', 'Selection pressure', 'Prevents resistance spread'),
      ],
    },
  }),

  gt('gcse-b6-7', 'selective-breeding-engineering', 'Selective Breeding & Genetic Engineering', {
    pageRef: 'B6.7',
    intro: 'Selective breeding (artificial selection): humans choose individuals with desired traits and breed them; offspring with the trait are bred again, etc. Used for thousands of years — modern wheat, dairy cattle, dog breeds. Genetic engineering: directly transferring genes from one organism to another. Tools: enzymes to cut + paste DNA, vectors (often viruses or bacterial plasmids). Examples: bacteria modified to make human insulin; golden rice (vitamin A added); herbicide-resistant crops. Concerns: ethics, ecological impact, allergens, gene flow to wild plants.',
    keyPoints: [
      'Selective breeding: choose parents → breed → repeat over generations',
      'Genetic engineering: cut and paste genes between species',
      'Insulin from bacteria — used to treat type 1 diabetes',
      'Golden rice: vitamin A for deficient diets',
      'GM crops: drought/pest/herbicide resistance',
      'Risks: ecological + ethical concerns; gene flow',
    ],
    glossary: [
      { term: 'Selective breeding', definition: 'Choosing parents with desired traits to produce offspring with those traits.' },
      { term: 'Genetic engineering', definition: 'Direct transfer of genes between organisms.' },
      { term: 'GM crop', definition: 'A genetically modified crop.' },
      { term: 'Vector', definition: 'A carrier (often a plasmid or virus) used to transfer DNA into another organism.' },
    ],
    practice: {
      warmUp: [
        cQ('b6-7.w1', 'Selective breeding is:', ['Random', 'Humans choose parents with desired traits', 'Cell division', 'Cloning'], 'Humans choose parents with desired traits', 'Artificial selection', 'Targeted', 'Breeders\' decision'),
      ],
      level1: [
        tQ('b6-7.1', 'Bacteria can be genetically modified to produce human ___.', 'insulin', '7 letters', 'Type 1 diabetes treatment', 'I______'),
      ],
      level2: [
        cQ('b6-7.2', 'Genetic engineering uses enzymes to:', ['Burn DNA', 'Cut and paste DNA', 'Generate light', 'Replicate organs'], 'Cut and paste DNA', 'Restriction + ligase', 'Tools of GE', 'Molecular scissors + glue'),
      ],
    },
  }),

  gt('gcse-b6-8', 'classification-evolutionary-trees', 'Classification & Evolutionary Trees', {
    pageRef: 'B6.8',
    intro: 'Linnaeus classified organisms into 7 ranks: kingdom, phylum, class, order, family, genus, species (mnemonic: "King Philip Came Over For Ginger Snaps"). Modern systems use 3 domains (Archaea, Bacteria, Eukaryota) based on DNA evidence. Binomial naming: genus + species, italicised (Homo sapiens). Evolutionary trees show relationships — closer branches = more recent common ancestor. Microscopy + DNA sequencing have revolutionised classification (e.g. fungi were once classed as plants; Archaea separated from Bacteria).',
    keyPoints: [
      'Linnaeus: K, P, C, O, F, G, S (King Philip Came Over For Ginger Snaps)',
      'Modern: 3 domains (Archaea, Bacteria, Eukaryota)',
      'Binomial naming: Genus species (italicised)',
      'Evolutionary trees show common ancestry',
      'DNA sequencing has reshaped classification',
    ],
    glossary: [
      { term: 'Species', definition: 'A group of organisms that can interbreed to produce fertile offspring.' },
      { term: 'Genus', definition: 'A group of closely related species.' },
      { term: 'Binomial naming', definition: 'Two-word naming system: Genus species.' },
      { term: 'Domain', definition: 'Highest rank in modern classification: Archaea, Bacteria, Eukaryota.' },
    ],
    practice: {
      warmUp: [
        cQ('b6-8.w1', 'Modern classification has how many domains?', ['1', '2', '3', '5'], '3', 'Archaea, Bacteria, Eukaryota', 'DNA-based', 'Three branches'),
      ],
      level1: [
        tQ('b6-8.1', 'Two-word scientific naming is the ___ system.', 'binomial', '8 letters', 'Bi = two, nomial = name', 'B_______'),
      ],
      level2: [
        cQ('b6-8.2', 'On an evolutionary tree, closer branches mean:', ['Same age', 'More recent common ancestor', 'Different ecosystem', 'Less DNA in common'], 'More recent common ancestor', 'Closer relationship', 'Fewer differences', 'Recent split'),
      ],
    },
  }),
];

// =============================================================
// B7 — Ecology
// =============================================================
export const b7EcologyTopics = [
  gt('gcse-b7-1', 'communities-abiotic-biotic', 'Communities, Abiotic & Biotic Factors', {
    pageRef: 'B7.1',
    intro: 'A community is all the populations of different species living together in a habitat. They depend on each other for food, shelter, pollination and seed dispersal — interdependence. Abiotic (non-living) factors: light intensity, temperature, water availability, soil pH, mineral content, oxygen levels (aquatic), wind. Biotic (living) factors: food availability, predators, competition, pathogens. A small change can cascade through the whole community.',
    keyPoints: [
      'Population: same species in same area',
      'Community: all populations together',
      'Ecosystem: community + abiotic environment',
      'Abiotic = non-living (light, temp, water, pH, O₂)',
      'Biotic = living (food, predators, competition, disease)',
      'Interdependence: change in one affects others',
    ],
    glossary: [
      { term: 'Population', definition: 'All members of one species in a given area.' },
      { term: 'Community', definition: 'All populations of different species in an area.' },
      { term: 'Ecosystem', definition: 'A community + its non-living environment.' },
      { term: 'Abiotic factor', definition: 'A non-living physical or chemical factor.' },
      { term: 'Biotic factor', definition: 'A living factor; involves other organisms.' },
    ],
    practice: {
      warmUp: [
        cQ('b7-1.w1', 'Soil pH is a:', ['Biotic factor', 'Abiotic factor', 'Population', 'Community'], 'Abiotic factor', 'Non-living', 'Chemical environment', 'Soil chemistry'),
      ],
      level1: [
        tQ('b7-1.1', 'All organisms of one species in an area form a ___.', 'population', '10 letters', 'P_________', 'One species only'),
      ],
      level2: [
        cQ('b7-1.2', 'Predator presence is a:', ['Abiotic factor', 'Biotic factor', 'Habitat', 'Population'], 'Biotic factor', 'Living', 'Other organism', 'Animal effect'),
      ],
    },
  }),

  gt('gcse-b7-2', 'adaptations', 'Adaptations', {
    pageRef: 'B7.2',
    intro: 'Adaptations are features that help an organism survive in its environment. Three categories: structural (body parts — camel\'s hump, polar bear\'s thick fur), behavioural (what it does — migration, hibernation, hunting in packs), functional (internal — desert kangaroo rats produce highly concentrated urine). Extremophiles live in extreme environments (very hot, very salty, very cold, very acidic) — useful in industry: thermophilic enzymes for laundry detergents work at high temperatures.',
    keyPoints: [
      'Structural: physical body features (e.g. polar bear fur)',
      'Behavioural: actions (e.g. migration, hibernation)',
      'Functional: internal/physiological (e.g. concentrated urine in desert mammals)',
      'Extremophiles: thrive in extreme environments — used in industry (e.g. thermophilic Bacillus enzymes in detergents)',
    ],
    glossary: [
      { term: 'Adaptation', definition: 'A feature that helps an organism survive in its environment.' },
      { term: 'Extremophile', definition: 'Organism living in extreme conditions (heat, salt, cold, acid).' },
    ],
    practice: {
      warmUp: [
        cQ('b7-2.w1', 'Camel hump is what type of adaptation?', ['Structural', 'Behavioural', 'Functional', 'None'], 'Structural', 'Body part', 'Physical', 'Visible feature'),
      ],
      level1: [
        cQ('b7-2.1', 'Migration is which type of adaptation?', ['Structural', 'Behavioural', 'Functional', 'Genetic'], 'Behavioural', 'Action', 'What it does', 'Movement'),
      ],
      level2: [
        tQ('b7-2.2', 'Organism living in very hot environments is a thermo___.', 'phile', 'Greek "loving"', '5 letters', 'P____'),
      ],
    },
  }),

  gt('gcse-b7-3', 'sampling-quadrats-transects', 'Field Sampling — Quadrats & Transects (RP6)', {
    pageRef: 'B7.3',
    intro: 'Counting every individual is impossible — ecologists sample. Quadrats: square frames placed randomly to count organisms in each (e.g. dandelions in a lawn). Mean per quadrat × total area / quadrat area = population estimate. Transects: a line across an environmental gradient (e.g. shore from sea to dunes); record what you find at fixed intervals along the line. Use random sampling to avoid bias; use a transect when you suspect distribution changes with a factor.',
    keyPoints: [
      'Quadrat: square frame, count organisms inside',
      'Total population = (mean per quadrat) × (total area / quadrat area)',
      'Sample randomly: random number generator for x,y coordinates',
      'Transect: line across an environmental gradient',
      'More quadrats = more reliable mean',
    ],
    we: {
      problem: 'A 10-quadrat sample gives a mean of 5 dandelions per 0.5 m × 0.5 m quadrat. Estimate the dandelion population in a 200 m² field.',
      steps: [
        { step: 1, instruction: 'Quadrat area', working: '0.5 × 0.5 = 0.25 m²' },
        { step: 2, instruction: 'Number of quadrats fitting field', working: '200 ÷ 0.25 = 800' },
        { step: 3, instruction: 'Population estimate', working: '5 × 800 = 4000 dandelions' },
      ],
      answer: '4000 dandelions',
      commonMistake: 'Forgetting to scale the quadrat area to the whole field area.',
    },
    rp: {
      title: 'Required Practical 6: Measuring population size of a common species in a habitat',
      aqaRef: 'AQA Required Practical (Biology) 9',
      aim: 'Estimate the population size of dandelions on a school field; investigate how distribution changes from shaded edge to open centre.',
      method: [
        'Generate 10 pairs of random numbers as x,y coordinates within the field.',
        'Place a 0.5 m × 0.5 m quadrat at each location and count dandelions.',
        'Calculate mean per quadrat. Multiply by (field area / quadrat area).',
        'For distribution: lay a 10 m transect from tree shadow into open field.',
        'Place quadrat every 1 m along the transect; record count.',
        'Plot count against distance from tree.',
      ],
      safety: 'Beware of ant or wasp nests; avoid pesticide-sprayed areas.',
      tips: [
        'More quadrats = more reliable mean. Aim for at least 10.',
        'Random sampling avoids bias from picking only easy-to-reach areas.',
      ],
    },
    glossary: [
      { term: 'Quadrat', definition: 'A square frame used to sample a fixed area.' },
      { term: 'Transect', definition: 'A line along which samples are taken at intervals.' },
      { term: 'Random sampling', definition: 'Choosing sample sites randomly to avoid bias.' },
    ],
    practice: {
      warmUp: [
        cQ('b7-3.w1', 'Why use random sampling?', ['Saves time', 'Avoids bias in choosing sites', 'Cheaper', 'Easier to analyse'], 'Avoids bias in choosing sites', 'Statistical reliability', 'Removes selection bias', 'Fair representation'),
      ],
      level1: [
        nQ('b7-3.1', 'Mean per 0.25 m² quadrat = 4. Estimate population in 100 m². Number only.', '1600', '100 ÷ 0.25 = 400', '4 × 400', '4 digits'),
      ],
      level2: [
        cQ('b7-3.2', 'A line of quadrats up a beach is a:', ['Transect', 'Random sample', 'Census', 'Population'], 'Transect', 'Across gradient', 'Straight line', 'For distribution patterns'),
      ],
    },
  }),

  gt('gcse-b7-4', 'food-chains-pyramids', 'Food Chains, Webs & Pyramids', {
    pageRef: 'B7.4',
    intro: 'Energy flow in ecosystems: producers (plants/algae) capture light energy → primary consumers (herbivores) → secondary consumers (carnivores) → tertiary consumers. At each level, only ~10% of energy is passed on (the rest is lost as heat, in faeces, or used for movement). Pyramids of biomass typically narrow upwards, illustrating the energy loss. Food webs show the interconnected feeding relationships in a community.',
    keyPoints: [
      'Producer → primary → secondary → tertiary consumer',
      '~90% energy lost at each transfer (heat, faeces, movement)',
      'Pyramids of biomass narrow upwards = decreasing biomass with trophic level',
      'Decomposers (bacteria, fungi) recycle nutrients from dead matter',
      'Apex predators are sensitive to ecosystem changes (bioaccumulation)',
    ],
    glossary: [
      { term: 'Producer', definition: 'Organism that makes its own food (usually photosynthesis).' },
      { term: 'Consumer', definition: 'Organism that eats other organisms.' },
      { term: 'Decomposer', definition: 'Organism (bacterium or fungus) that breaks down dead matter.' },
      { term: 'Trophic level', definition: 'A position in a food chain (level 1 = producer, level 2 = primary consumer, etc.).' },
      { term: 'Biomass', definition: 'Total dry mass of organisms.' },
    ],
    practice: {
      warmUp: [
        cQ('b7-4.w1', 'Producers are usually:', ['Animals', 'Plants', 'Bacteria', 'Fungi'], 'Plants', 'Make own food', 'Photosynthesise', 'Trophic level 1'),
      ],
      level1: [
        nQ('b7-4.1', '%energy passed on at each trophic level (typical)?', '10', '90% lost', 'Hot, faeces, movement', '2 digits'),
      ],
      level2: [
        tQ('b7-4.2', 'Organisms breaking down dead matter are ___.', 'decomposers', '11 letters', 'Recycle nutrients', 'D__________'),
      ],
    },
  }),

  gt('gcse-b7-5', 'water-carbon-cycles', 'Water & Carbon Cycles', {
    pageRef: 'B7.5',
    intro: 'Materials are recycled in nature. Water cycle: evaporation/transpiration → condensation → precipitation → runoff/percolation → back to oceans. Carbon cycle: CO₂ taken in by photosynthesis → carbon in organisms → returned by respiration of animals/plants/decomposers, by combustion of fossil fuels, and by decay. Burning fossil fuels has thrown the cycle out of balance, raising atmospheric CO₂ → climate change.',
    keyPoints: [
      'Water: evaporation → condensation → precipitation → flow back',
      'Plants release water by transpiration',
      'Carbon in: photosynthesis (CO₂ → glucose)',
      'Carbon out: respiration (all organisms), combustion, decay',
      'Burning fossil fuels: extra CO₂ → climate change',
    ],
    glossary: [
      { term: 'Transpiration', definition: 'Loss of water from plant leaves.' },
      { term: 'Combustion', definition: 'Burning fuel; releases CO₂ + H₂O.' },
      { term: 'Decomposition', definition: 'Breakdown of dead organic matter by microorganisms.' },
    ],
    practice: {
      warmUp: [
        cQ('b7-5.w1', 'Water leaves plants via:', ['Photosynthesis', 'Transpiration', 'Combustion', 'Pollination'], 'Transpiration', 'Leaf stomata', 'Water vapour', 'T___________'),
      ],
      level1: [
        cQ('b7-5.1', 'Carbon enters living things by:', ['Respiration', 'Photosynthesis', 'Decomposition', 'Combustion'], 'Photosynthesis', 'CO₂ → glucose', 'Plants take in CO₂', 'Light reaction'),
      ],
      level2: [
        cQ('b7-5.2', 'Burning fossil fuels affects the carbon cycle by:', ['Reducing CO₂', 'Adding extra CO₂ to atmosphere', 'Adding O₂', 'No effect'], 'Adding extra CO₂ to atmosphere', 'Long-stored carbon released', 'Climate driver', 'Anthropogenic'),
      ],
    },
  }),

  gt('gcse-b7-6', 'biodiversity-human-impact', 'Biodiversity & Human Impact', {
    pageRef: 'B7.6',
    intro: 'Biodiversity = variety of species in an ecosystem. High biodiversity = stable ecosystem. Threats: deforestation (logging, agriculture), pollution (water — sewage and fertiliser run-off; air — sulfur dioxide, particulates; land — landfill), peat bog destruction (releases CO₂, destroys habitats), global warming. Conservation: breeding endangered species, protected reserves, reforestation, regulated fishing quotas, recycling.',
    keyPoints: [
      'Biodiversity = variety of species',
      'High biodiversity = ecosystem stability',
      'Pollution sources: sewage, fertilisers, fossil fuel combustion, plastic',
      'Deforestation: habitat loss + CO₂ release + reduced photosynthesis',
      'Peat bogs store carbon; destruction releases CO₂',
      'Conservation: breeding programmes, reserves, regulated quotas, recycling',
    ],
    glossary: [
      { term: 'Biodiversity', definition: 'Variety of species in an ecosystem.' },
      { term: 'Deforestation', definition: 'Large-scale cutting down of forests.' },
      { term: 'Eutrophication', definition: 'Algal bloom from fertiliser run-off; depletes water oxygen.' },
    ],
    practice: {
      warmUp: [
        cQ('b7-6.w1', 'High biodiversity makes an ecosystem:', ['Less stable', 'More stable', 'No effect', 'Cooler'], 'More stable', 'Resilience', 'More food-web routes', 'Stability principle'),
      ],
      level1: [
        tQ('b7-6.1', 'Algal bloom from fertiliser run-off is called ___.', 'eutrophication', '14 letters', 'E_____________', 'Excess nutrients'),
      ],
      level2: [
        cQ('b7-6.2', 'Why preserve peat bogs?', ['Aesthetic', 'They store carbon — destruction releases CO₂', 'They are warm', 'They have no plants'], 'They store carbon — destruction releases CO₂', 'Carbon sink', 'Climate impact', 'Locked-up CO₂'),
      ],
    },
  }),

  gt('gcse-b7-7', 'food-security-trophic', 'Trophic Levels & Food Security', {
    pageRef: 'B7.7',
    tier: 'higher',
    intro: 'Food security = having enough food for everyone. Threats: increasing population, changing diets in wealthy nations, environmental change, costly agricultural inputs, conflict, pests/pathogens. Solutions: efficient farming (factory farming raises ethical issues), sustainable fisheries (quotas, net mesh size), GM crops, biological control of pests. Eating lower in the food chain (more plant-based diets) is more efficient: less energy is lost, more people can be fed per hectare.',
    keyPoints: [
      'Food security threats: population growth, costly inputs, conflict, climate change',
      'Factory farming: efficient but raises ethics',
      'Sustainable fishing: quotas + minimum net-mesh size',
      'Eating plants directly = more efficient than eating animals',
      'Biological control + GM crops: tools to maintain food supply',
    ],
    glossary: [
      { term: 'Food security', definition: 'Reliable access to sufficient nutritious food.' },
      { term: 'Sustainable fishing', definition: 'Fishing at a rate that allows fish stocks to replenish.' },
      { term: 'Biological control', definition: 'Using natural predators/pathogens to control pests.' },
    ],
    practice: {
      warmUp: [
        cQ('b7-7.w1', 'Eating more plants (lower trophic level) means:', ['Less efficient', 'More efficient — less energy lost', 'No difference', 'More CO₂'], 'More efficient — less energy lost', '90% lost per level', 'Closer to producers', 'Fewer transfers'),
      ],
      level1: [
        tQ('b7-7.1', 'Using a natural predator to control a pest is called ___ control.', 'biological', '10 letters', 'B_________', 'Living vs chemical'),
      ],
      level2: [
        cQ('b7-7.2', 'Why are fishing quotas used?', ['Reduce profit', 'Allow fish stocks to recover', 'Lower fish prices', 'Help shipbuilders'], 'Allow fish stocks to recover', 'Sustainability', 'Prevent collapse', 'Maintain populations'),
      ],
    },
  }),
];

// =============================================================
// Section bundles (export for scienceContent.js)
// =============================================================
export const allBiologyGCSEBundles = {
  b1: b1CellBiologyTopics,
  b2: b2OrganisationTopics,
  b3: b3InfectionResponseTopics,
  b4: b4BioenergeticsTopics,
  b5: b5HomeostasisTopics,
  b6: b6InheritanceTopics,
  b7: b7EcologyTopics,
};
